<script>
  import Layout from '../components/Layout.svelte';
  import { onMount, tick } from 'svelte';
  import { navigate, useLocation } from 'svelte-routing';
  import { createCurrencyFormatter } from '../lib/currency.js';
  import { user } from '../stores/authStore';
  import { normalizeForComparison, findVendorCandidates, parseCreatedVendor, isRequiredCustomFieldResult, mergeRequiredCustomFields, customFieldValuesFromSelections, requiredCustomFieldsFilled } from '../lib/vendorMatch.js';
  import { filterExpenseAccounts, loadXeroChartAccounts } from '../lib/settings/xeroAccountFilters.js';
  import RequiredVendorCustomField from '../components/RequiredVendorCustomField.svelte';
  import SyncResults from './SyncResults.svelte';
  import { PROVINCES, calculateCanadaTax, planCanadaPurchaseTax, normalizeProvince } from '../lib/canadaTax.js';
  import { buildDirectBillJtPayload } from '../lib/directBillJobTreadPayload.js';
  import CostItemPickerList from '../components/CostItemPickerList.svelte';
  import { resolveBillDueDate, describeDueTerms } from '../lib/dueTerms.js';
  import { costTypeLabel, listCostTypes, matchesCostTypes, buildSplitTargets, itemsForTarget, computeProRataSplit } from '../lib/costSplit.js';
  import { hasLineTaxOverrides, planLineTax, sumLineTax } from '../lib/lineTaxRates.js';

  const location = useLocation();
  
  let selectedBills = [];
  let currentBillIndex = 0;
  let syncing = false;
  let syncProgress = { current: 0, total: 0, step: '' };
  let syncResults = [];
  let syncResultsByUploadId = {};
  let syncResultsEl;
  let extractedColEl;
  let matchingColEl;
  let error = null;
  let pdfZoom = 90;
  let extractedData = {};
  let originalTotal = 0;
  let totalOverridden = false;
  let readOnly = false;
  let clearingSynced = false;
  let clearedSyncedNote = false;
  
  // Validation state
  let isLineItemsValid = true;
  let isTotalValid = true;
  let isDueDatePast = false;
  // True while the due date shown came from the org's default terms rather
  // than the supplier's bill, so the field can say so and follow issue-date edits.
  let dueDateDefaulted = false;
  
  // Job/PO matching state
  let isJobMode = true;
  let customers = [];
  let filteredCustomers = [];
  let customerSearchQuery = '';
  let selectedCustomer = null;
  let showCustomerDropdown = false;
  let customerInputElement;

  let vendorOrders = [];
  let filteredVendorOrders = [];
  let vendorOrderSearchQuery = '';
  let selectedVendorOrder = null;
  let showVendorOrderDropdown = false;
  let vendorOrderInputElement;
  let isLoadingVendorOrders = false;

  let matchedJob = null;
  let matchedPO = null;

  // Cost groups state
  let jobCostGroups = [];
  let poCostGroups = [];
  let isLoadingCostGroups = false;
  let costGroupsCached = false;
  let costGroupsCachedAt = null;

  // Active lens for the assignment search dropdown ('costGroup' or 'costCode').
  let activeLens = 'costGroup';

  // Assignment dropdown variables (per line-item index)
  let assignmentSearchQueries = {};
  let assignmentDropdownOpen = {};

  // Line item assignment state - maps line item index to cost item id
  let lineItemAssignments = {};

  // Cost type filter (Labor / Materials / ...) from JobTread's cost types.
  // Empty = show everything. Kept across bills in the session and remembered
  // per browser, since it's a working preference rather than bill data.
  let costTypeFilter = loadCostTypeFilter();
  // Bulk assign: tick lines, then assign them all to one budget item.
  let bulkSelected = {};
  let bulkDropdownOpen = false;
  // Whole-bill split: the bill is spread over every budget item in one cost
  // group or cost code, pro-rata by each item's budget.
  let splitMode = false;
  let splitTarget = null;          // { kind: 'costGroup' | 'costCode', id, label }
  let splitTargetQuery = '';
  let splitTargetDropdownOpen = false;

  // Vendor matching state
  let jtVendors = [];
  let filteredVendors = [];
  let vendorSearchQuery = '';
  let selectedVendorId = null;
  let showVendorDropdown = false;
  let isLoadingVendors = false;
  let autoMatchedVendor = false;
  let isAutoMatchingVendor = false;
  let autoMatchFailed = false;
  let isCreatingVendor = false;
  let pendingVendorCandidates = [];
  let pendingVendorSourceName = '';
  let requiredVendorFields = [];
  let requiredVendorFieldValues = {};
  let requiredVendorError = '';
  let vendorAliases = {};
  let vendorAliasesLoaded = false;

  // Canada: the org's province drives GST / PST / HST / QST. Null = not
  // Canadian, or no province chosen yet (older single-rate behaviour).
  let caProvince = null;

  // Cost code mappings & bill settings
  let costCodeMappings = {};
  let billSettings = {};
  let markAsPaid = false;
  let userExpenseCode = '';
  const currencyOptions = ['NZD', 'AUD', 'USD', 'GBP', 'CAD', 'EUR'];
  let accountingPlatform = 'xero';
  $: isQuickBooks = accountingPlatform === 'quickbooks';
  // Accounting destination label adapts to the user's platform (Xero vs QuickBooks).
  $: overheadDest = isQuickBooks ? 'QuickBooks' : 'Xero';
  $: overheadDestShort = isQuickBooks ? 'QB' : 'Xero';

  // --- Accounting-only (overhead) routing ---
  // When on, the bill/receipt is an overhead (e.g. fuel) and is synced to Xero ONLY,
  // excluded from JobTread. Bills from a saved "overhead vendor" auto-tick this.
  let accountingOnly = false;
  let overheadVendors = {};          // { [jtVendorId]: vendorName }
  let overheadVendorsLoaded = false;
  let lastOverheadCheckedVendor = null;
  // Per-bill overhead expense account: each overhead bill picks its own Xero
  // expense code from a list of the org's expense accounts (fuel → Motor Vehicle,
  // etc.). Empty = fall back to the org default expense code.
  // For QuickBooks orgs this holds a QBO Account Id rather than a Xero code —
  // a user is on one platform or the other, so the field is shared rather than
  // persisting a second one on every bill.
  let overheadAccountCode = '';
  let xeroExpenseAccounts = [];      // [{ code, name }] active EXPENSE accounts from Xero
  let xeroExpenseAccountsLoaded = false;
  // Overhead bills going to QuickBooks are matched against QUICKBOOKS vendors,
  // not JobTread's — the bill never touches JobTread, so a JT vendor id is
  // meaningless to it and the JT list may not even contain the supplier.
  let qboVendors = [];               // [{ id, displayName }]
  let qboVendorsLoaded = false;
  let isLoadingQboVendors = false;
  let selectedQboVendorId = null;

  // Xero contact for this bill (Xero orgs). Direct Bill never creates a Xero
  // contact on its own: it's matched (remembered / exact / Ltd-insensitive), or
  // the user picks one from the closest candidates or a search, or chooses to
  // create a new contact on purpose.
  let xeroContact = null;              // { contactID, name, how }
  let xeroContactCandidates = [];
  let xeroContactLoading = false;
  let xeroContactError = '';
  let xeroContactCreateNew = false;
  let xeroContactQuery = '';
  let xeroContactSearchResults = [];
  let showXeroContactDropdown = false;
  let xeroContactKey = '';
  // A contact saved with the approval payload. Applied once when this bill
  // loads, so the rematch search does not wipe the reviewer's choice.
  let pendingXeroContact = null;
  let xeroContactTimer = null;
  let xeroContactSearchTimer = null;
  let qboVendorQuery = '';
  let showQboVendorDropdown = false;
  let qboExpenseAccounts = [];       // [{ id, name }] Classification=Expense accounts from QBO
  let qboExpenseAccountsLoaded = false;
  let qboConnected = false;
  let qboDefaultAccountId = '';
  let qboPaymentAccounts = [];       // Bank / Credit Card accounts an expense can be paid FROM
  let qboPaymentAccountId = '';      // per-bill override of the org default
  let qboDefaultPaymentAccountId = '';

  $: totalModified = originalTotal !== 0 && Math.abs(Math.abs(extractedData.total || 0) - Math.abs(originalTotal)) > 0.01;

  // Lazily load the expense accounts the first time overhead is turned on,
  // from whichever platform this org is on.
  $: if (accountingOnly && !isQuickBooks && !xeroExpenseAccountsLoaded) {
    loadXeroExpenseAccounts();
  }
  $: if (accountingOnly && isQuickBooks && !qboExpenseAccountsLoaded) {
    loadQboExpenseAccounts();
  }
  // Ticking "send overhead to QB only" is itself the trigger to go and find the
  // vendor. Previously nothing resolved a vendor until a job or PO matched, so
  // an overhead bill — which has neither — left the vendor blank.
  $: if (qboOverheadMode && !qboVendorsLoaded && !isLoadingQboVendors) {
    loadQboVendors();
  }
  $: isDueDatePast = false;
  $: activeCostGroups = isJobMode ? jobCostGroups : poCostGroups;
  // Accounting-only has no job — the extracted-data panel is editable once the mode is on.
  // Overhead + QuickBooks is a distinct mode: no job, no PO, and the vendor
  // comes from QuickBooks rather than JobTread.
  $: qboOverheadMode = accountingOnly && isQuickBooks;
  $: matchingComplete = accountingOnly ? true : (activeCostGroups.length > 0 && (matchedJob || matchedPO));
  // Job/PO selected but budget returned empty — UI would otherwise look like "waiting on job selection"
  $: noBudgetLoaded = !accountingOnly && !isLoadingCostGroups && activeCostGroups.length === 0 && (
    isJobMode ? !!matchedJob : !!matchedPO
  );
  $: allLineItemsAssigned = extractedData.lineItems && extractedData.lineItems.length > 0 &&
    extractedData.lineItems.every((_, i) => lineItemAssignments[i]);

  // --- Cost type filter ---
  $: availableCostTypes = listCostTypes(activeCostGroups);
  $: showCostTypeFilter = availableCostTypes.length > 1;
  // Drop filter values this budget doesn't have, so a stale choice can't hide everything.
  $: effectiveCostTypes = costTypeFilter.filter(t => availableCostTypes.includes(t));

  function loadCostTypeFilter() {
    try {
      const v = JSON.parse(localStorage.getItem('bz.directBill.costTypeFilter') || '[]');
      return Array.isArray(v) ? v.filter(x => typeof x === 'string') : [];
    } catch { return []; }
  }

  function saveCostTypeFilter() {
    try { localStorage.setItem('bz.directBill.costTypeFilter', JSON.stringify(costTypeFilter)); } catch {}
  }

  function toggleCostType(type) {
    costTypeFilter = effectiveCostTypes.includes(type)
      ? effectiveCostTypes.filter(t => t !== type)
      : [...effectiveCostTypes, type];
    saveCostTypeFilter();
  }

  function clearCostTypeFilter() {
    costTypeFilter = [];
    saveCostTypeFilter();
  }

  // --- Bulk assign ---
  $: bulkSelectedIndexes = Object.keys(bulkSelected).filter(k => bulkSelected[k]).map(Number);
  $: allLinesBulkSelected = (extractedData.lineItems || []).length > 0
    && bulkSelectedIndexes.length === extractedData.lineItems.length;

  function toggleBulkLine(index) {
    bulkSelected = { ...bulkSelected, [index]: !bulkSelected[index] };
  }

  function toggleAllBulkLines() {
    const next = {};
    if (!allLinesBulkSelected) (extractedData.lineItems || []).forEach((_, i) => { next[i] = true; });
    bulkSelected = next;
  }

  function bulkAssign(costItem) {
    for (const i of bulkSelectedIndexes) lineItemAssignments[i] = costItem.id;
    lineItemAssignments = { ...lineItemAssignments };
    bulkSelected = {};
    bulkDropdownOpen = false;
    assignmentSearchQueries = { ...assignmentSearchQueries, bulk: '' };
  }

  // --- Whole-bill split ---
  // The amount spread is the ex-tax sum of the lines JobTread would receive,
  // the same base the per-line path sends.
  $: splitBase = roundHalfUp((extractedData.lineItems || [])
    .filter(li => (li.quantity ?? 1) !== 0 || (li.amount || 0) !== 0)
    .reduce((sum, li) => sum + (li.amount || 0), 0));
  $: splitTargets = buildSplitTargets(activeCostGroups, effectiveCostTypes, cleanCostGroupName);
  $: splitTargetList = (() => {
    const q = splitTargetQuery.trim().toLowerCase();
    const lists = activeLens === 'costCode'
      ? [['Cost codes', splitTargets.codes], ['Cost groups', splitTargets.groups]]
      : [['Cost groups', splitTargets.groups], ['Cost codes', splitTargets.codes]];
    return lists
      .map(([heading, targets]) => ({ heading, targets: q ? targets.filter(t => t.label.toLowerCase().includes(q)) : targets }))
      .filter(section => section.targets.length > 0);
  })();
  $: splitResult = splitMode && splitTarget
    ? computeProRataSplit(itemsForTarget(activeCostGroups, splitTarget, effectiveCostTypes), splitBase)
    : { lines: [], evenSplit: false };
  $: assignmentsComplete = splitMode ? splitResult.lines.length > 0 : allLineItemsAssigned;

  function selectSplitTarget(target) {
    splitTarget = { kind: target.kind, id: target.id, label: target.label };
    splitTargetQuery = '';
    splitTargetDropdownOpen = false;
  }

  function setSplitMode(on) {
    splitMode = on;
    if (!on) splitTarget = null;
    bulkSelected = {};
  }
  $: hasDates = !!(extractedData.issueDate && extractedData.dueDate);

  function isResultFullySynced(result) {
    if (!result) return false;
    // A QuickBooks result is present for both success and failure. Locking on
    // accountingOnly alone greyed out the review screen after a rejected push.
    if (result.quickbooks) return !!result.quickbooks.success;
    const xeroSkipped = result.xero === null;
    return (xeroSkipped || !!result.xero?.success) && (!!result.jobtread?.success || !!result.accountingOnly);
  }

  // Results + lock state must follow the bill on screen. Using the global
  // syncResults array leaked the first bill's success onto the next one
  // (Sync stayed "Already synced" / greyed out after Next).
  $: currentBillUploadId = selectedBills[currentBillIndex]?.uploadId;
  $: syncResults = (currentBillUploadId && syncResultsByUploadId[currentBillUploadId]) || [];
  $: currentBillSynced = syncResults.some(isResultFullySynced);
  // Xero already created the bill — re-running syncBoth would hit "already synced" and wipe the Open links.
  $: xeroAlreadyPushed = !!syncResults[0]?.xero?.success;
  $: syncLocked = currentBillSynced || xeroAlreadyPushed;
  // Overhead bills need only a vendor + dates (no job, no line assignment).
  // In QuickBooks-overhead mode a vendor NAME is enough — the push creates the
  // vendor in QuickBooks if it doesn't match one. Elsewhere a JobTread vendor
  // id is still required.
  $: vendorReady = qboOverheadMode
    ? !!(selectedQboVendorId || (extractedData.vendorName || '').trim())
    : !!selectedVendorId;
  $: readyToSync = vendorReady && xeroContactReady && hasDates && !syncing && !syncLocked && !qboPaidNeedsAccount && (
    accountingOnly ? true : ((matchedJob || matchedPO) && assignmentsComplete)
  );

  async function setSyncResults(results, uploadId = results[0]?.uploadId || currentBillUploadId) {
    if (uploadId) {
      syncResultsByUploadId = { ...syncResultsByUploadId, [uploadId]: results };
    }
    if (results.length > 0) {
      await tick();
      syncResultsEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  // Auto-tick accounting-only when the selected vendor is a saved overhead vendor.
  // Runs once per vendor change so a manual untick isn't overridden.
  $: if (selectedVendorId && selectedVendorId !== lastOverheadCheckedVendor) {
    lastOverheadCheckedVendor = selectedVendorId;
    if (overheadVendors[selectedVendorId]) accountingOnly = true;
  }

  /** Normalize extracted dates to YYYY-MM-DD for HTML date inputs. */
  function toDateInputValue(dateStr) {
    if (dateStr == null || dateStr === '') return '';
    const s = String(dateStr).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
    const slashMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const [, day, month, year] = slashMatch;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      return d.toISOString().split('T')[0];
    }
    return '';
  }

  function normalizeExtractedData(data) {
    return {
      discount: 0,
      ...data,
      issueDate: toDateInputValue(data.issueDate),
      dueDate: toDateInputValue(data.dueDate),
      lineItems: data.lineItems ? data.lineItems.map(item => ({
        ...item,
        discount: item.discount ?? 0
      })) : []
    };
  }

  function setExtractedDate(field, value) {
    extractedData = { ...extractedData, [field]: value };
    if (field === 'dueDate') {
      dueDateDefaulted = false;
    } else if (field === 'issueDate' && dueDateDefaulted) {
      applyDefaultDueDate();
    }
  }

  // Fill (or, with override on, replace) the due date from the default terms
  // in Settings > Bill Sync. Never touches a synced or read-only bill.
  function applyDefaultDueDate() {
    if (readOnly || !extractedData || !billSettings.directBillDueTerms) return;
    // Check the bill on screen directly: the reactive syncLocked can still
    // reflect the previous bill for a tick after Next.
    const bill = selectedBills[currentBillIndex];
    if (!bill || bill.status === 'synced') return;
    if ((syncResultsByUploadId[bill.uploadId] || []).some(isResultFullySynced)) return;
    const current = dueDateDefaulted ? '' : extractedData.dueDate;
    const { dueDate, defaulted } = resolveBillDueDate(
      { issueDate: extractedData.issueDate, dueDate: current },
      billSettings.directBillDueTerms
    );
    if (defaulted) {
      extractedData = { ...extractedData, dueDate };
      dueDateDefaulted = true;
    }
  }

  onMount(async () => {
    if ($location?.state?.selectedBills) {
      selectedBills = $location.state.selectedBills;
      readOnly = $location.state.readOnly === true;
      autoPrefillAttemptedForBill = -1;
      
      for (let i = 0; i < selectedBills.length; i++) {
        const bill = selectedBills[i];
        try {
          const response = await fetch(`/api/direct-bills/${bill.uploadId}`);
          const data = await response.json();
          if (response.ok && data.success) {
            selectedBills[i] = { ...bill, ...data.upload };
          }
        } catch (err) {
          console.error(`Error fetching details for ${bill.uploadId}:`, err);
        }
      }
      selectedBills = [...selectedBills];
      loadBillData(currentBillIndex);
      lastLoadedBillIndex = currentBillIndex;
    } else {
      navigate('/direct-bill-import');
      return;
    }

    try {
      const [mappingsRes, userRes] = await Promise.all([
        fetch('/api/users/cost-code-mappings'),
        fetch('/api/user')
      ]);
      if (mappingsRes.ok) {
        const mappingsData = await mappingsRes.json();
        costCodeMappings = mappingsData.mappings || {};
      }
      if (userRes.ok) {
        const userData = await userRes.json();
        if (userData.success && userData.user) {
          billSettings = userData.user.billSettings || {};
          userExpenseCode = userData.user['xeroexpense-code'] || '';
          accountingPlatform = userData.user.accountingPlatform === 'quickbooks' ? 'quickbooks' : 'xero';
          caProvince = userData.user.country === 'CA' ? normalizeProvince(userData.user.province) : null;
          isJobMode = billSettings.billmodedefaultjob || false;
          markAsPaid = billSettings.markDirectImportBillsAsPaid === true;
          // The first bill loaded before settings arrived.
          initCanadaTax();
          if (userData.user.assignmentLensDefault === 'costCode') {
            activeLens = 'costCode';
          } else {
            activeLens = 'costGroup';
          }
          // The first bill loaded before settings arrived.
          applyDefaultDueDate();
        }
      }
    } catch (err) {
      console.error('Error loading user settings:', err);
    }

    await loadOverheadVendors();
    await fetchAllJobs();
  });

  async function loadOverheadVendors() {
    if (overheadVendorsLoaded) return;
    try {
      const response = await fetch('/api/users/overhead-vendors');
      if (response.ok) {
        const data = await response.json();
        overheadVendors = data.overheadVendors || {};
      }
    } catch (err) {
      console.warn('Could not load overhead vendors:', err.message);
    }
    overheadVendorsLoaded = true;
  }

  // Load the org's Xero expense accounts (cached) to populate the overhead picker.
  // Uses the shared filter (type ∈ EXPENSE/OVERHEADS/DIRECTCOSTS) — Xero's `class`
  // isn't returned by the chart-accounts endpoint, so we key off `type`.
  async function loadXeroExpenseAccounts() {
    if (xeroExpenseAccountsLoaded || isQuickBooks) return;
    xeroExpenseAccountsLoaded = true; // set first so the reactive doesn't re-fire
    try {
      const { accounts } = await loadXeroChartAccounts();
      xeroExpenseAccounts = filterExpenseAccounts(accounts)
        .map(a => ({ code: a.code, name: a.name }))
        .sort((a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true }));
    } catch (err) {
      console.warn('Could not load Xero expense accounts:', err.message);
    }
  }

  // Load the org's QuickBooks expense accounts for the overhead picker, plus
  // the configured default so the "use the default" option can name it.
  async function loadQboExpenseAccounts() {
    if (qboExpenseAccountsLoaded || !isQuickBooks) return;
    qboExpenseAccountsLoaded = true; // set first so the reactive doesn't re-fire
    try {
      const statusRes = await fetch('/api/qbo/status');
      const statusData = await statusRes.json();
      qboConnected = !!statusData?.connected;
      qboDefaultAccountId = statusData?.overheadSettings?.defaultOverheadAccountId || '';
      qboDefaultPaymentAccountId = statusData?.overheadSettings?.paymentAccountId || '';
      if (!qboConnected) return;

      const res = await fetch('/api/qbo/accounts');
      const data = await res.json();
      if (data.success) {
        qboExpenseAccounts = data.accounts || [];
        qboPaymentAccounts = data.paymentAccounts || [];
      }
    } catch (err) {
      console.warn('Could not load QuickBooks expense accounts:', err.message);
    }
  }

  $: qboDefaultAccountName = qboExpenseAccounts.find(a => String(a.id) === String(qboDefaultAccountId))?.name || '';
  $: qboDefaultPaymentAccountName = qboPaymentAccounts.find(a => String(a.id) === String(qboDefaultPaymentAccountId))?.name || '';
  // An already-paid overhead becomes a QuickBooks Expense, which must name the
  // account the money left. No account anywhere = the push would be refused.
  $: qboPaidNeedsAccount = qboOverheadMode && markAsPaid && !extractedData.isCreditNote
    && !qboPaymentAccountId && !qboDefaultPaymentAccountId;
  // What this bill will become in QuickBooks, for the button and the hints.
  $: qboDocLabel = extractedData.isCreditNote
    ? 'Vendor Credit'
    : (markAsPaid ? 'Expense' : 'Bill');

  // ---- Xero contact matching (Xero orgs only) ----
  $: xeroContactNeeded = !isQuickBooks && !readOnly;
  $: xeroContactReady = !xeroContactNeeded || !!xeroContact || xeroContactCreateNew;
  // What the sync sends: the chosen contact, or "create new" when picked.
  $: xeroContactChoice = xeroContact
    ? { contactID: xeroContact.contactID, name: xeroContact.name }
    : (xeroContactCreateNew ? { create: true } : null);

  // Re-match when the bill or the supplier name on it changes (debounced so
  // typing in the supplier name doesn't fire a search per key).
  $: {
    const key = xeroContactNeeded && currentBillUploadId ? `${currentBillUploadId}|${(extractedData?.vendorName || '').trim()}` : '';
    if (key !== xeroContactKey) {
      xeroContactKey = key;
      const saved = pendingXeroContact;
      pendingXeroContact = null;
      xeroContactCandidates = [];
      xeroContactError = '';
      clearTimeout(xeroContactTimer);
      if (saved?.contactID) {
        xeroContact = { contactID: saved.contactID, name: saved.name, how: 'chosen' };
        xeroContactCreateNew = false;
      } else if (saved?.create) {
        xeroContact = null;
        xeroContactCreateNew = true;
      } else {
        xeroContact = null;
        xeroContactCreateNew = false;
        if (key && (extractedData?.vendorName || '').trim()) {
          xeroContactTimer = setTimeout(() => loadXeroContactMatch(key), 400);
        }
      }
    }
  }

  async function loadXeroContactMatch(key) {
    const name = (extractedData?.vendorName || '').trim();
    xeroContactLoading = true;
    try {
      const res = await fetch(`/api/direct-bills/xero-contact-match?name=${encodeURIComponent(name)}`);
      const data = await res.json().catch(() => ({}));
      if (key !== xeroContactKey) return; // bill/name changed meanwhile
      if (res.ok && data.success) {
        xeroContact = data.contact ? { ...data.contact, how: data.how } : null;
        xeroContactCandidates = data.candidates || [];
      } else {
        xeroContactError = data.error || 'Could not search Xero contacts.';
      }
    } catch (err) {
      if (key === xeroContactKey) xeroContactError = 'Could not search Xero contacts.';
    } finally {
      if (key === xeroContactKey) xeroContactLoading = false;
    }
  }

  // The user picked a contact: use it, and remember it for this supplier name.
  async function chooseXeroContact(contact) {
    xeroContact = { contactID: contact.contactID, name: contact.name, how: 'chosen' };
    xeroContactCreateNew = false;
    xeroContactQuery = '';
    showXeroContactDropdown = false;
    const sourceName = (extractedData?.vendorName || '').trim();
    if (sourceName) {
      fetch('/api/direct-bills/xero-contact-alias', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceName, contactID: contact.contactID, contactName: contact.name })
      }).catch(() => {});
    }
  }

  function clearXeroContact() {
    xeroContact = null;
    xeroContactCreateNew = false;
  }

  function onXeroContactSearch() {
    clearTimeout(xeroContactSearchTimer);
    const q = xeroContactQuery.trim();
    if (q.length < 2) { xeroContactSearchResults = []; return; }
    xeroContactSearchTimer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/direct-bills/xero-contacts?q=${encodeURIComponent(q)}`);
        const data = await res.json().catch(() => ({}));
        if (q === xeroContactQuery.trim()) xeroContactSearchResults = data.contacts || [];
      } catch (_) { xeroContactSearchResults = []; }
    }, 300);
  }

  // Pull the QuickBooks vendor list and try to match this bill's supplier to it.
  async function loadQboVendors() {
    if (isLoadingQboVendors) return;
    isLoadingQboVendors = true;
    try {
      const res = await fetch('/api/qbo/vendors');
      const data = await res.json();
      if (data.success) {
        qboVendors = data.vendors || [];
        qboVendorsLoaded = true;
        autoMatchQboVendor();
      } else {
        // Not connected / no default account yet — leave the picker empty and let
        // the panel explain why rather than failing silently.
        qboVendorsLoaded = true;
      }
    } catch (err) {
      console.warn('Could not load QuickBooks vendors:', err.message);
      qboVendorsLoaded = true;
    } finally {
      isLoadingQboVendors = false;
    }
  }

  // Case/space-insensitive match on DisplayName — the same comparison QuickBooks
  // itself uses to reject duplicate vendors.
  function autoMatchQboVendor() {
    if (selectedQboVendorId) return;
    const name = (extractedData.vendorName || '').trim().toLowerCase();
    if (!name) return;
    const hit = qboVendors.find(v => v.displayName.trim().toLowerCase() === name);
    if (hit) {
      selectedQboVendorId = hit.id;
      qboVendorQuery = hit.displayName;
    }
  }

  function selectQboVendor(vendor) {
    selectedQboVendorId = vendor.id;
    qboVendorQuery = vendor.displayName;
    // Keep the bill's vendor name in step so the push and the review panel agree.
    extractedData = { ...extractedData, vendorName: vendor.displayName };
    showQboVendorDropdown = false;
  }

  function clearQboVendor() {
    selectedQboVendorId = null;
    qboVendorQuery = '';
  }

  // The search box is a filter, not a rename. Once the text no longer matches
  // the pinned vendor, drop the id so the push cannot send a different supplier
  // from the one the banner is showing.
  function onQboVendorQueryInput(event) {
    if (!selectedQboVendorId) return;
    const next = event.target.value;
    const selected = qboVendors.find(v => String(v.id) === String(selectedQboVendorId));
    if (!selected || selected.displayName !== next) {
      selectedQboVendorId = null;
    }
  }

  $: filteredQboVendors = (() => {
    const q = (qboVendorQuery || '').trim().toLowerCase();
    if (!q) return qboVendors.slice(0, 50);
    return qboVendors.filter(v => v.displayName.toLowerCase().includes(q)).slice(0, 50);
  })();

  // Mark / unmark the currently-matched vendor as "always overhead" (persisted).
  async function toggleOverheadVendor(makeOverhead) {
    if (!selectedVendorId) return;
    const vendorName = jtVendors.find(v => v.id === selectedVendorId)?.name
      || vendorSearchQuery || String(selectedVendorId);
    try {
      if (makeOverhead) {
        await fetch('/api/users/overhead-vendors', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vendorId: selectedVendorId, vendorName })
        });
        overheadVendors = { ...overheadVendors, [selectedVendorId]: vendorName };
        accountingOnly = true;
      } else {
        await fetch('/api/users/overhead-vendors', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vendorId: selectedVendorId })
        });
        const next = { ...overheadVendors };
        delete next[selectedVendorId];
        overheadVendors = next;
      }
    } catch (err) {
      console.warn('Failed to update overhead vendor:', err.message);
    }
  }

  $: currentBill = selectedBills[currentBillIndex];
  
  let lastLoadedBillIndex = -1;
  // Guard to ensure we attempt reference-based PO prefill only once per bill load
  let autoPrefillAttemptedForBill = -1;
  // Set when the user changes mode or clears/picks a match, so auto-prefill
  // cannot flip the mode back or refill a selection they rejected.
  let userTouchedMatch = false;
  // Bumped on user override and on each new bill. In-flight prefill captures
  // the value and drops its results if it has changed.
  let matchEpoch = 0;

  $: if (currentBillIndex >= 0 && selectedBills.length > 0 && currentBillIndex !== lastLoadedBillIndex) {
    loadBillData(currentBillIndex);
    lastLoadedBillIndex = currentBillIndex;
  }

  // One-time auto-prefill of PO mode job+PO when extracted reference looks like JOB-PO (e.g. CANT01-116)
  $: if (
    customers.length > 0 &&
    (extractedData?.poNumber || extractedData?.reference) &&
    !matchedJob &&
    !matchedPO &&
    !userTouchedMatch &&
    lastLoadedBillIndex >= 0 &&
    autoPrefillAttemptedForBill !== lastLoadedBillIndex
  ) {
    autoPrefillAttemptedForBill = lastLoadedBillIndex;
    // Defer to allow any reviewPayload replay and state updates to settle
    setTimeout(() => {
      tryAutoPrefillFromReference();
    }, 60);
  }

  function markMatchTouched() {
    userTouchedMatch = true;
    matchEpoch += 1;
  }

  function prefillStillCurrent(epoch) {
    return epoch == null || (epoch === matchEpoch && !userTouchedMatch);
  }

  function resetBillUiState() {
    userTouchedMatch = false;
    matchEpoch += 1;
    lineItemAssignments = {};
    bulkSelected = {};
    bulkDropdownOpen = false;
    splitMode = false;
    splitTarget = null;
    splitTargetQuery = '';
    splitTargetDropdownOpen = false;
    assignmentSearchQueries = {};
    assignmentDropdownOpen = {};
    matchedJob = null;
    matchedPO = null;
    selectedCustomer = null;
    selectedVendorOrder = null;
    customerSearchQuery = '';
    vendorOrderSearchQuery = '';
    jobCostGroups = [];
    poCostGroups = [];
    selectedVendorId = null;
    autoMatchedVendor = false;
    autoMatchFailed = false;
    isAutoMatchingVendor = false;
    pendingVendorCandidates = [];
    pendingVendorSourceName = '';
    requiredVendorFields = [];
    requiredVendorFieldValues = {};
    requiredVendorError = '';
    vendorSearchQuery = '';
    markAsPaid = billSettings.markDirectImportBillsAsPaid === true;
    error = null;
    accountingOnly = false;
    overheadAccountCode = '';
    selectedQboVendorId = null;
    qboVendorQuery = '';
    qboPaymentAccountId = '';
    lastOverheadCheckedVendor = null;
    extractedData = {};
    dueDateDefaulted = false;
    originalTotal = 0;
    totalOverridden = false;
    if (extractedColEl) extractedColEl.scrollTop = 0;
    if (matchingColEl) matchingColEl.scrollTop = 0;
    if (typeof window !== 'undefined') window.scrollTo({ top: 0 });
  }

  function loadBillData(billIndex) {
    const bill = selectedBills[billIndex];
    // Always clear the previous bill's match/edit state first. Sync results
    // follow currentBillUploadId via the reactive statement above, so we
    // must not leave vendor/job/line assignments from bill N on bill N+1.
    resetBillUiState();
    if (!bill?.extractedData) return;

    const data = bill.extractedData;
    originalTotal = data.originalInvoiceTotal ?? data.total ?? 0;
    totalOverridden = data.totalOverridden === true;
    extractedData = normalizeExtractedData(data);
    accountingOnly = data.accountingOnly === true;
    overheadAccountCode = data.overheadAccountCode || '';
    initCanadaTax();
    applyDefaultDueDate();

    if (bill.reviewPayload) {
      replayReviewPayload(bill).then(() => startAutoReviewForCurrentBill());
    } else {
      startAutoReviewForCurrentBill();
    }

    setTimeout(() => validateInvoice(), 0);
  }

  /** Same auto-match path the first bill gets on mount (job from reference + vendor). */
  function startAutoReviewForCurrentBill() {
    if (readOnly) return;

    if (matchedJob || matchedPO) {
      if (!selectedVendorId) autoMatchVendor();
      return;
    }

    if (accountingOnly) {
      autoMatchVendor();
      return;
    }

    // Jobs already loaded (bill 2+). Mark this index so the reactive doesn't double-run.
    // If customers aren't ready yet (first bill), leave the guard alone so it fires later.
    if (customers.length > 0) {
      autoPrefillAttemptedForBill = currentBillIndex;
      setTimeout(() => tryAutoPrefillFromReference(), 60);
    }
  }

  async function replayReviewPayload(bill) {
    const payload = bill.reviewPayload;

    // Set before any await. Loading the bill changes the supplier key, and
    // that search would otherwise replace the contact the reviewer chose.
    const savedContact = payload.xeroContact
      || payload.enrichment?.[bill.uploadId]?.xeroContact
      || null;
    if (savedContact?.contactID || savedContact?.create) {
      pendingXeroContact = savedContact;
    }

    if (payload.extractedData) {
      const data = payload.extractedData;
      originalTotal = data.originalInvoiceTotal ?? data.total ?? 0;
      totalOverridden = data.totalOverridden === true;
      extractedData = normalizeExtractedData(data);
    }

    if (payload.isJobMode !== undefined) {
      isJobMode = payload.isJobMode;
    }
    if (payload.accountingOnly !== undefined) {
      accountingOnly = payload.accountingOnly === true;
    }
    if (payload.overheadAccountCode !== undefined) {
      overheadAccountCode = payload.overheadAccountCode || '';
    }
    if (payload.activeLens) {
      activeLens = payload.activeLens;
    }

    if (payload.matchedJob) {
      matchedJob = payload.matchedJob;
      selectedCustomer = payload.matchedJob.jobId;
      customerSearchQuery = payload.matchedJob.jobName || '';
      await fetchJobCostGroups(payload.matchedJob.jobId);
    } else if (payload.matchedPO) {
      selectedCustomer = payload.matchedPO.jobId;
      customerSearchQuery = customers.find(c => c.id === payload.matchedPO.jobId)?.name || '';
      matchedPO = payload.matchedPO;
      await fetchJobCostGroups(payload.matchedPO.jobId);
    }

    if (payload.lineItemAssignments) {
      lineItemAssignments = { ...payload.lineItemAssignments };
    }
    if (payload.splitMode && payload.splitTarget) {
      splitMode = true;
      splitTarget = payload.splitTarget;
      // The split was worked out under this type filter; restore it so the
      // approver sees the same allocation.
      if (Array.isArray(payload.splitCostTypes)) costTypeFilter = payload.splitCostTypes;
    }

    if (payload.selectedVendorId) {
      selectedVendorId = payload.selectedVendorId;
      autoMatchedVendor = true;
    }

    if (payload.markAsPaid !== undefined) {
      markAsPaid = payload.markAsPaid === true;
    }

    if (payload.selectedQboVendorId) {
      selectedQboVendorId = payload.selectedQboVendorId;
      qboVendorQuery = payload.qboVendorName || '';
    }
    if (payload.qboPaymentAccountId) {
      qboPaymentAccountId = payload.qboPaymentAccountId;
    }

    setTimeout(() => validateInvoice(), 0);
  }

  async function clearSyncedStatus(uploadId = currentBill?.uploadId) {
    if (!uploadId || clearingSynced) return;
    clearingSynced = true;
    error = null;
    try {
      const response = await fetch(`/api/direct-bills/${uploadId}/clear-synced-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Could not clear synced status');
      }

      const billIndex = selectedBills.findIndex(b => b.uploadId === uploadId);
      if (billIndex !== -1) {
        selectedBills[billIndex] = {
          ...selectedBills[billIndex],
          ...(data.upload || {}),
          status: 'completed',
          xeroBillId: null,
          qboBillId: null,
          syncedAt: null,
          syncedToXero: false,
          syncedToJT: false
        };
        selectedBills = [...selectedBills];
      }
      readOnly = false;
      clearedSyncedNote = true;
      await setSyncResults([], uploadId);
    } catch (err) {
      console.error('Error clearing synced status:', err);
      error = err.message;
    } finally {
      clearingSynced = false;
    }
  }

  async function saveCurrentBillData() {
    if (!currentBill || !extractedData) return;
    try {
      const dataToSave = {
        ...extractedData,
        accountingOnly,
        overheadAccountCode,
        originalInvoiceTotal: originalTotal,
        totalOverridden
      };
      extractedData = dataToSave;
      const response = await fetch(`/api/direct-bills/${currentBill.uploadId}/extracted-data`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ extractedData: dataToSave })
      });
      if (response.ok) {
        const billIndex = selectedBills.findIndex(b => b.uploadId === currentBill.uploadId);
        if (billIndex !== -1) selectedBills[billIndex].extractedData = dataToSave;
      }
    } catch (err) {
      console.error('Error saving edited data:', err);
    }
  }

  async function prevBill() {
    if (currentBillIndex === 0 || syncing) return;
    // Skip the extracted-data PUT after a successful sync: that endpoint
    // rewrites status back to "completed", and awaiting it blocked Next.
    if (!syncLocked) await saveCurrentBillData();
    currentBillIndex--;
  }
  async function nextBill() {
    if (currentBillIndex >= selectedBills.length - 1 || syncing) return;
    if (!syncLocked) await saveCurrentBillData();
    currentBillIndex++;
  }

  function addLineItem() {
    if (!extractedData.lineItems) extractedData.lineItems = [];
    extractedData.lineItems.push({ description: '', quantity: 1, unitPrice: 0, discount: 0, amount: 0 });
    extractedData.lineItems = [...extractedData.lineItems];
    recalculateTotal();
    validateInvoice();
  }

  function removeLineItem(index) {
    extractedData.lineItems.splice(index, 1);
    extractedData.lineItems = [...extractedData.lineItems];
    const newAssignments = {};
    Object.entries(lineItemAssignments).forEach(([k, v]) => {
      const ki = parseInt(k);
      if (ki < index) newAssignments[ki] = v;
      else if (ki > index) newAssignments[ki - 1] = v;
    });
    lineItemAssignments = newAssignments;
    bulkSelected = {};
    recalculateTotal();
  }

  function roundHalfUp(num, decimals = 2) {
    const multiplier = Math.pow(10, decimals);
    const shifted = num * multiplier;
    const floored = Math.floor(shifted);
    const decimal = shifted - floored;
    if (decimal >= 0.5 - Number.EPSILON) return Math.ceil(shifted) / multiplier;
    return floored / multiplier;
  }

  function calculateLineTotal(qty, unitPrice, discount) {
    return roundHalfUp(qty * unitPrice * (1 - discount / 100));
  }

  function updateLineItemAmount(index) {
    const item = extractedData.lineItems[index];
    item.amount = roundHalfUp(item.quantity * item.unitPrice * (1 - (item.discount || 0) / 100));
    extractedData.lineItems = [...extractedData.lineItems];
    recalculateTotal();
  }

  function recalculateTotal() {
    if (extractedData.lineItems && extractedData.lineItems.length > 0) {
      extractedData.subtotal = roundHalfUp(extractedData.lineItems.reduce((sum, item) => sum + (item.amount || 0), 0));
    }
    if (hasLineTaxOverrides(extractedData.lineItems)) {
      applyLineTaxTotals();
      return;
    }
    if (caTaxActive && extractedData.taxBreakdownAuto) {
      extractedData.taxBreakdown = provinceBreakdown(extractedData.subtotal);
      extractedData.taxAmount = sumBreakdown(extractedData.taxBreakdown);
    }
    if (!totalOverridden) {
      extractedData.total = roundHalfUp((extractedData.subtotal || 0) + (extractedData.taxAmount || 0));
    }
    extractedData = { ...extractedData };
    validateInvoice();
  }

  function onTotalInput() {
    totalOverridden = true;
    extractedData.totalOverridden = true;
    extractedData = { ...extractedData };
    validateInvoice();
  }

  function clearTotalOverride() {
    totalOverridden = false;
    extractedData.totalOverridden = false;
    recalculateTotal();
  }

  // --- Canadian tax (GST / PST / HST / QST) ---
  // extractedData.taxBreakdown holds the reviewed rows [{ code, rate, amount }];
  // taxAmount is always their sum. taxBreakdownAuto = rows follow the province
  // rates as the subtotal changes; editing a row or taking the bill's figures
  // turns that off. billTaxAmount keeps the tax printed on the bill.
  $: caTaxActive = !!caProvince && Array.isArray(extractedData.taxBreakdown);
  $: caTaxMismatch = caTaxActive && extractedData.billTaxAmount != null
    && Math.abs((extractedData.taxAmount || 0) - (extractedData.billTaxAmount || 0)) > 0.05;

  function provinceBreakdown(subtotal) {
    return calculateCanadaTax(subtotal || 0, caProvince).components.map(({ code, rate, amount }) => ({ code, rate, amount }));
  }

  function sumBreakdown(rows) {
    return roundHalfUp((rows || []).reduce((s, r) => s + (Number(r.amount) || 0), 0));
  }

  // First look at a bill: work its tax out from the province rates. A bill
  // already reviewed (has a breakdown) is left as saved.
  function initCanadaTax() {
    if (!caProvince || readOnly || !extractedData?.lineItems) return;
    if (Array.isArray(extractedData.taxBreakdown) || hasLineTaxOverrides(extractedData.lineItems)) return;
    const printedTax = Number(extractedData.taxAmount) || 0;
    if (!extractedData.hasTax && printedTax <= 0) return;
    const taxBreakdown = provinceBreakdown(extractedData.subtotal);
    extractedData = {
      ...extractedData,
      hasTax: true,
      billTaxAmount: printedTax,
      taxBreakdown,
      taxBreakdownAuto: true
    };
    applyBreakdownTotals();
  }

  function applyBreakdownTotals() {
    const rows = extractedData.taxBreakdown || [];
    extractedData.taxAmount = sumBreakdown(rows);
    extractedData.taxRate = roundHalfUp(rows.reduce((s, r) => s + (Number(r.rate) || 0), 0), 3);
    if (!totalOverridden) {
      extractedData.total = roundHalfUp((extractedData.subtotal || 0) + extractedData.taxAmount);
    }
    extractedData = { ...extractedData };
    validateInvoice();
  }

  function setBreakdownAmount(index, value) {
    const rows = [...(extractedData.taxBreakdown || [])];
    rows[index] = { ...rows[index], amount: Number(value) || 0 };
    extractedData = { ...extractedData, taxBreakdown: rows, taxBreakdownAuto: false };
    applyBreakdownTotals();
  }

  function recalculateProvinceTax() {
    extractedData = { ...extractedData, taxBreakdown: provinceBreakdown(extractedData.subtotal), taxBreakdownAuto: true };
    applyBreakdownTotals();
  }

  // Take the tax as printed on the bill (e.g. an out-of-province supplier
  // charging GST only): its own tax lines if they were read, else one row.
  function useBillTax() {
    const lines = Array.isArray(extractedData.taxLines) ? extractedData.taxLines.filter(l => l.amount || l.rate) : [];
    const rows = lines.length
      ? lines.map(l => ({ code: l.code || 'TAX', rate: Number(l.rate) || 0, amount: Number(l.amount) || 0 }))
      : [{ code: 'TAX', rate: roundHalfUp(((extractedData.billTaxAmount || 0) / (extractedData.subtotal || 1)) * 100, 3), amount: extractedData.billTaxAmount || 0 }];
    extractedData = { ...extractedData, taxBreakdown: rows, taxBreakdownAuto: false };
    applyBreakdownTotals();
  }

  // --- Per-line tax rates ---
  // The reviewer can set any line's tax rate (the "Tax %" box on the line), the
  // workaround for bills that mix rates (exempt lines, a GST-only line from an
  // out-of-province supplier). Once any line has its own rate the bill is taxed
  // line by line (lib/lineTaxRates.js): the other lines stay at the bill's rate,
  // kept in lineTaxDefaultRate, and lineTaxBaseline keeps the tax as it was so
  // "Reset line tax rates" can put it back.
  $: lineTaxMode = hasLineTaxOverrides(extractedData.lineItems);
  $: lineTaxPlan = lineTaxMode ? planLineTax(extractedData.lineItems, lineTaxOptions(extractedData, caProvince)) : null;
  $: lineTaxPst = lineTaxPlan ? roundHalfUp(lineTaxPlan.reduce((s, p) => s + p.nonRecoverable, 0)) : 0;
  $: defaultLineTaxRate = lineTaxMode
    ? (Number(extractedData.lineTaxDefaultRate) || 0)
    : billTaxRatePct(extractedData, caTaxActive);

  // The rate the bill's lines are taxed at: Canada = the reviewed breakdown's
  // combined rate, elsewhere the extracted rate, else tax / subtotal.
  function billTaxRatePct(data, caActive) {
    const tax = Number(data.taxAmount) || 0;
    if (!data.hasTax && tax <= 0) return 0;
    if (caActive) return roundHalfUp((data.taxBreakdown || []).reduce((s, r) => s + (Number(r.rate) || 0), 0), 3);
    const rate = Number(data.taxRate);
    // Extraction stores a percentage (15 means 15%). A stored 1 is 1%, not 100%.
    if (rate > 0) return rate;
    const subtotal = Number(data.subtotal) || 0;
    return subtotal ? roundHalfUp((tax / subtotal) * 100, 3) : 0;
  }

  // Canada with PST: a line's rate up to the province's GST rate is GST, the
  // rest is PST (BC 12% = GST 5 + PST 7; 5% = GST only).
  function lineTaxOptions(data, province) {
    const provincePlan = province ? planCanadaPurchaseTax({ province, subtotal: 0 }) : null;
    return {
      defaultRatePct: Number(data.lineTaxDefaultRate) || 0,
      recoverableRatePct: provincePlan?.hasPst ? provincePlan.recoverableRatePct : null
    };
  }

  function setLineTaxRate(index, value) {
    const raw = value === '' || value == null ? null : Number(value);
    const rate = Number.isFinite(raw) && raw >= 0 ? raw : null;
    const lines = [...(extractedData.lineItems || [])];
    if (rate !== null && !hasLineTaxOverrides(lines)) {
      // First line rate: the other lines keep the bill's rate.
      extractedData.lineTaxDefaultRate = billTaxRatePct(extractedData, caTaxActive);
      extractedData.lineTaxBaseline = {
        taxAmount: Number(extractedData.taxAmount) || 0,
        hasTax: !!extractedData.hasTax
      };
    }
    lines[index] = { ...lines[index], taxRateOverride: rate };
    extractedData.lineItems = lines;
    if (hasLineTaxOverrides(lines)) applyLineTaxTotals();
    else resetLineTaxRates();
  }

  function applyLineTaxTotals() {
    const plan = planLineTax(extractedData.lineItems, lineTaxOptions(extractedData, caProvince));
    extractedData.taxAmount = sumLineTax(plan);
    extractedData.hasTax = extractedData.taxAmount !== 0;
    if (!totalOverridden) {
      extractedData.total = roundHalfUp((extractedData.subtotal || 0) + extractedData.taxAmount);
    }
    extractedData = { ...extractedData };
    validateInvoice();
  }

  function resetLineTaxRates() {
    const baseline = extractedData.lineTaxBaseline || {};
    const { lineTaxDefaultRate, lineTaxBaseline, ...rest } = extractedData;
    extractedData = {
      ...rest,
      lineItems: (rest.lineItems || []).map(({ taxRateOverride, ...line }) => line),
      taxAmount: baseline.taxAmount ?? rest.taxAmount,
      hasTax: baseline.hasTax ?? rest.hasTax
    };
    if (caTaxActive && !extractedData.taxBreakdownAuto) applyBreakdownTotals();
    else recalculateTotal();
  }

  function validateLineItems() {
    if (!extractedData.lineItems || extractedData.lineItems.length === 0) return true;
    const lineItemsSum = roundHalfUp(extractedData.lineItems.reduce((sum, item) => {
      return sum + calculateLineTotal(item.quantity || 0, item.unitPrice || 0, item.discount || 0);
    }, 0));
    const expectedSubtotal = roundHalfUp(extractedData.subtotal * (1 - (extractedData.discount || 0) / 100));
    const lineCount = extractedData.lineItems.length;
    const tolerance = Math.max(0.02, lineCount * 0.01);
    return Math.abs(lineItemsSum - expectedSubtotal) <= tolerance;
  }

  function validateTotal() {
    const calculatedTotal = Math.round(((extractedData.subtotal || 0) + (extractedData.taxAmount || 0)) * 100) / 100;
    return Math.abs(calculatedTotal - (extractedData.total || 0)) <= 0.01;
  }

  function validateDueDate() {
    if (!extractedData.dueDate) return false;
    const due = new Date(extractedData.dueDate);
    return isNaN(due.getTime());
  }

  function validateInvoice() {
    isLineItemsValid = validateLineItems();
    isTotalValid = validateTotal();
    isDueDatePast = validateDueDate();
  }

  function validateBill(data) {
    if (!data?.vendorName?.trim()) return { valid: false, error: 'Vendor name is required' };
    const total = Number(data.total);
    if (data.isCreditNote) {
      if (!Number.isFinite(total) || Math.abs(total) === 0) {
        return { valid: false, error: 'Credit note total must not be zero' };
      }
    } else if (!Number.isFinite(total) || total <= 0) {
      return { valid: false, error: 'Total amount must be greater than 0' };
    }
    if (!data.lineItems?.length) return { valid: false, error: 'At least one line item is required' };
    return { valid: true };
  }

  $: _currencyFmt = createCurrencyFormatter($user?.country);
  $: formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '';
    return _currencyFmt(amount);
  };

  async function goBack() {
    if (!readOnly && !syncLocked) {
      await saveCurrentBillData();
    }
    navigate('/direct-bill-import');
  }

  // --- Job/PO Matching (cloned from SyncSelectedBillsfromXero) ---

  async function fetchAllJobs() {
    try {
      let allJobs = [];
      let nextPage = null;
      do {
        const response = await fetch(`/api/jt/jobs${nextPage ? `?page=${nextPage}` : ''}`);
        const data = await response.json();
        if (data.jobs) allJobs = [...allJobs, ...data.jobs];
        nextPage = data.nextPage;
      } while (nextPage);
      customers = allJobs;
      filteredCustomers = customers;
    } catch (err) {
      console.error('Error fetching jobs:', err);
    }
  }

  /**
   * Parse a reference string that may be in JOB-PO format.
   * Examples: "CANT01-116", "CANT01 116", "JOB42-7", "Wright Residence"
   * A space separates a PO only when the rest is a number, so a job name
   * like "Wright Residence" stays one job part.
   * Returns { jobPart, poPart } where poPart may be null if not detectable.
   */
  function parseJobPoReference(ref) {
    if (!ref) return { jobPart: null, poPart: null };
    const s = String(ref).trim();
    if (!s) return { jobPart: null, poPart: null };

    // Prefer explicit dash separator
    const dashIdx = s.indexOf('-');
    if (dashIdx > 0) {
      const jobPart = s.slice(0, dashIdx).trim();
      const poPart = s.slice(dashIdx + 1).trim();
      return { jobPart: jobPart || null, poPart: poPart || null };
    }

    // Space separates a PO only when the remainder is a vendor-order number.
    const spaceIdx = s.indexOf(' ');
    if (spaceIdx > 0) {
      const jobPart = s.slice(0, spaceIdx).trim();
      const poPart = s.slice(spaceIdx + 1).trim();
      if (/^\d+$/.test(poPart)) {
        return { jobPart: jobPart || null, poPart };
      }
    }

    // Try to split trailing digits as PO, leading alphanum as job code
    // e.g. "CANT01116" -> not reliable; only do if clear transition letter<->digit at end
    const m = s.match(/^([A-Za-z0-9]+?)(\d{1,6})$/);
    if (m) {
      return { jobPart: m[1], poPart: m[2] };
    }

    // Fallback: whole string is job-ish, no PO part
    return { jobPart: s, poPart: null };
  }

  /**
   * Find a job from customers by matching a job code/part against job.number or job.name.
   */
  function findJobForPart(jobPart) {
    if (!jobPart || !customers.length) return null;
    const q = jobPart.toLowerCase();

    // 1) Exact match on job.number (preferred)
    let match = customers.find(c => (c.number || '').toLowerCase() === q);
    if (match) return match;

    // 2) job.number starts with or includes the part (e.g. job "CANT01-XYZ", part "CANT01")
    match = customers.find(c => {
      const num = (c.number || '').toLowerCase();
      return num && (num === q || num.startsWith(q) || num.includes(q));
    });
    if (match) return match;

    // 3) name contains the part (case-insensitive)
    match = customers.find(c => (c.name || '').toLowerCase().includes(q));
    if (match) return match;

    // 4) name starts with the part
    match = customers.find(c => (c.name || '').toLowerCase().startsWith(q));
    return match || null;
  }

  /**
   * One-time attempt to prefill job (and PO, when the reference names a real
   * vendor order) from the extracted reference. Stays in the user's mode
   * unless that vendor order exists. Bails once the user overrides the match.
   */
  async function tryAutoPrefillFromReference() {
    if (readOnly || userTouchedMatch) return;
    // Bail if something got matched in the meantime (e.g. reviewPayload replay)
    if (matchedJob || matchedPO) return;

    const epoch = matchEpoch;
    const live = () => prefillStillCurrent(epoch) && !matchedJob && !matchedPO;

    // Prefer poNumber (labeled "PO / Reference" in UI), also accept a top-level reference if present
    const ref = (extractedData && (extractedData.poNumber || extractedData.reference)) || '';
    const { jobPart, poPart } = parseJobPoReference(ref);
    if (!jobPart || !live()) return;

    console.log(`🔎 Auto-prefill: reference="${ref}" -> jobPart="${jobPart}", poPart="${poPart || ''}"`);

    const job = findJobForPart(jobPart);
    if (!job) {
      console.log(`🔎 Auto-prefill: no job matched for part "${jobPart}"`);
      return;
    }
    if (!live()) return;

    console.log(`🔎 Auto-prefill: matched job`, { id: job.id, name: job.name, number: job.number });

    // A PO-shaped reference switches mode only when that vendor order exists.
    if (poPart) {
      await fetchVendorOrders(job.id, epoch);
      if (!live()) return;

      const order = (vendorOrders || []).find(o => {
        if (o == null) return false;
        const n = o.number;
        if (n == null) return false;
        return String(n) === String(poPart);
      });

      if (order) {
        console.log(`🔎 Auto-prefill: matched PO #${order.number} for job ${job.name}`);
        if (isJobMode) isJobMode = false;
        if (!prefillStillCurrent(epoch)) return;
        await selectCustomer(job.id, epoch);
        if (!prefillStillCurrent(epoch)) return;
        await selectVendorOrder(order.id, epoch);
        return;
      }

      console.log(`🔎 Auto-prefill: no PO #${poPart} on ${job.name}; staying in ${isJobMode ? 'Job' : 'PO'} mode`);
    }

    if (!prefillStillCurrent(epoch)) return;
    await selectCustomer(job.id, epoch);
  }

  function handleCustomerSearch(event) {
    customerSearchQuery = event.target.value;
    filterCustomers();
  }

  function filterCustomers() {
    if (!customerSearchQuery || customerSearchQuery.trim() === '') {
      filteredCustomers = customers;
    } else {
      const query = customerSearchQuery.toLowerCase().trim();
      filteredCustomers = customers.filter(c => c.name.toLowerCase().includes(query));
    }
  }

  function clearCustomerSelection() {
    markMatchTouched();
    selectedCustomer = null;
    customerSearchQuery = '';
    matchedJob = null;
    matchedPO = null;
    selectedVendorOrder = null;
    vendorOrderSearchQuery = '';
    vendorOrders = [];
    filteredVendorOrders = [];
    jobCostGroups = [];
    poCostGroups = [];
    lineItemAssignments = {};
    assignmentSearchQueries = {};
    assignmentDropdownOpen = {};
    filteredCustomers = customers;
    showCustomerDropdown = true;
    customerInputElement?.focus();
  }

  function clearVendorOrderSelection() {
    markMatchTouched();
    selectedVendorOrder = null;
    vendorOrderSearchQuery = '';
    matchedPO = null;
    poCostGroups = [];
    lineItemAssignments = {};
    assignmentSearchQueries = {};
    assignmentDropdownOpen = {};
    filteredVendorOrders = vendorOrders;
    showVendorOrderDropdown = true;
    vendorOrderInputElement?.focus();
  }

  async function selectCustomer(customerId, epoch = null) {
    // A click from the list or the mode toggle is an override. Auto-prefill
    // passes its epoch and must not count as one.
    if (epoch == null) markMatchTouched();
    if (!prefillStillCurrent(epoch)) return;

    selectedCustomer = customerId;
    const cust = customers.find(c => c.id === customerId);
    if (cust) customerSearchQuery = cust.name;
    showCustomerDropdown = false;

    if (isJobMode) {
      matchedJob = {
        jobId: customerId,
        jobName: cust?.name || '',
        jobNumber: cust?.number != null ? String(cust.number) : ''
      };
      matchedPO = null;
      await fetchJobCostGroups(customerId, epoch);
      if (!prefillStillCurrent(epoch)) return;
      await autoMatchVendor();
    } else {
      matchedJob = null;
      matchedPO = null;
      jobCostGroups = [];
      poCostGroups = [];
      await fetchVendorOrders(customerId, epoch);
    }
  }

  async function fetchVendorOrders(customerId, epoch = null) {
    isLoadingVendorOrders = true;
    selectedVendorOrder = null;
    vendorOrders = [];
    try {
      let allOrders = [];
      let nextPage = null;
      do {
        const url = `/api/jt/documents?jtjobid=${customerId}${nextPage ? `&page=${nextPage}` : ''}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch vendor orders');
        const data = await response.json();
        if (Array.isArray(data)) {
          allOrders = [...allOrders, ...data];
          nextPage = null;
        } else if (data.orders && Array.isArray(data.orders)) {
          allOrders = [...allOrders, ...data.orders];
          nextPage = data.nextPage;
        } else {
          break;
        }
      } while (nextPage);
      if (!prefillStillCurrent(epoch)) return;
      vendorOrders = allOrders;
      filteredVendorOrders = vendorOrders;
    } catch (err) {
      console.error('Error fetching vendor orders:', err);
    } finally {
      isLoadingVendorOrders = false;
    }
  }

  function handleVendorOrderSearch(event) {
    vendorOrderSearchQuery = event.target.value;
    filterVendorOrders();
  }

  function filterVendorOrders() {
    if (!vendorOrderSearchQuery || vendorOrderSearchQuery.trim() === '') {
      filteredVendorOrders = vendorOrders;
    } else {
      const query = vendorOrderSearchQuery.toLowerCase().trim();
      filteredVendorOrders = vendorOrders.filter(o =>
        o.number.toString().toLowerCase().includes(query) ||
        (o.subject && o.subject.toLowerCase().includes(query))
      );
    }
  }

  async function selectVendorOrder(orderId, epoch = null) {
    if (epoch == null) markMatchTouched();
    if (!prefillStillCurrent(epoch)) return;

    selectedVendorOrder = orderId;
    const order = vendorOrders.find(o => o.id === orderId);
    if (order) {
      vendorOrderSearchQuery = `PO #${order.number}`;
      const poJob = customers.find(c => c.id === selectedCustomer);
      matchedPO = {
        vendorOrderId: order.id,
        vendorOrderNumber: order.number,
        jobId: selectedCustomer,
        jobName: poJob?.name || '',
        jobNumber: poJob?.number != null ? String(poJob.number) : ''
      };
      matchedJob = null;
    }
    showVendorOrderDropdown = false;

    if (selectedCustomer) {
      await fetchPOCostGroups(orderId, epoch);
      if (!prefillStillCurrent(epoch)) return;
      await autoMatchVendor();
    }
  }

  async function fetchPOCostGroups(vendorOrderId, epoch = null) {
    isLoadingCostGroups = true;
    lineItemAssignments = {};
    splitTarget = null;
    bulkSelected = {};
    assignmentSearchQueries = {};
    assignmentDropdownOpen = {};
    try {
      const response = await fetch(`/api/jt/lineitems?jtjobid=${vendorOrderId}`);
      if (response.ok) {
        const data = await response.json();
        if (!prefillStillCurrent(epoch)) return;
        poCostGroups = (data.costGroups || [])
          .filter(group => !/- Bill .+ - (Job Mode|PO #)/i.test(group.name || ''))
          .map(group => ({
            ...group,
            jobId: group.jobId || null,
            parentCostGroup: group.parentCostGroup || null
          }));
      }
    } catch (err) {
      console.error('Error fetching PO cost groups:', err);
    } finally {
      isLoadingCostGroups = false;
    }
  }

  async function fetchJobCostGroups(jobId, epoch = null) {
    isLoadingCostGroups = true;
    lineItemAssignments = {};
    splitTarget = null;
    bulkSelected = {};
    assignmentSearchQueries = {};
    assignmentDropdownOpen = {};
    try {
      const response = await fetch(`/api/jt/jobs/${jobId}/cost-groups-with-items`);
      if (response.ok) {
        const data = await response.json();
        if (!prefillStillCurrent(epoch)) return;
        jobCostGroups = (data.costGroups || [])
          .filter(group => !/- Bill .+ - (Job Mode|PO #)/i.test(group.name || ''))
          .map(group => ({
            ...group,
            jobId: group.jobId || null,
            parentCostGroup: group.parentCostGroup || null
          }));
        costGroupsCached = data.cached || false;
        costGroupsCachedAt = data.cachedAt || null;
      }
    } catch (err) {
      console.error('Error fetching cost groups:', err);
    } finally {
      isLoadingCostGroups = false;
    }
  }

  async function refreshCostGroups() {
    if (!isJobMode && matchedPO?.vendorOrderId) {
      await fetchPOCostGroups(matchedPO.vendorOrderId);
      return;
    }
    const jobId = matchedJob?.jobId || matchedPO?.jobId;
    if (!jobId) return;
    isLoadingCostGroups = true;
    try {
      const response = await fetch(`/api/jt/jobs/${jobId}/cost-groups-with-items?refresh=true`);
      if (response.ok) {
        const data = await response.json();
        jobCostGroups = (data.costGroups || [])
          .filter(group => !/- Bill .+ - (Job Mode|PO #)/i.test(group.name || ''))
          .map(group => ({
            ...group,
            jobId: group.jobId || null,
            parentCostGroup: group.parentCostGroup || null
          }));
        costGroupsCached = false;
        costGroupsCachedAt = null;
      }
    } catch (err) {
      console.error('Error refreshing cost groups:', err);
    } finally {
      isLoadingCostGroups = false;
    }
  }

  function cleanCostGroupName(name) {
    if (!name) return name;
    return name
      .replace(/[-\s]*#[\u4e00-\u9fff]+$/g, '')
      .replace(/[\u4e00-\u9fff]+/g, '')
      .replace(/[-\s]+$/, '')
      .trim();
  }

  function getAllCostItems() {
    const items = [];
    const groups = isJobMode ? jobCostGroups : poCostGroups;
    for (const group of groups) {
      const groupItems = group.costItems || group.lineItems || [];
      for (const item of groupItems) {
        items.push({ ...item, costGroupId: group.id, costGroupName: group.name });
      }
    }
    return items;
  }

  function assignLineItem(lineIndex, costItemId) {
    if (costItemId) {
      lineItemAssignments[lineIndex] = costItemId;
    } else {
      delete lineItemAssignments[lineIndex];
    }
    lineItemAssignments = { ...lineItemAssignments };
  }

  function selectAssignmentItem(lineIndex, item, groupIndex) {
    lineItemAssignments[lineIndex] = item.id;
    lineItemAssignments = { ...lineItemAssignments };
    assignmentDropdownOpen[lineIndex] = false;
    assignmentDropdownOpen = { ...assignmentDropdownOpen };
  }

  // --- Assignment filtering (Cost Group lens) ---

  function getFilteredAssignmentItems(lineIndex) {
    const searchQuery = (assignmentSearchQueries[lineIndex] || '').trim();
    const lowerQuery = searchQuery.toLowerCase();

    let sourceCostGroups = isJobMode ? jobCostGroups : poCostGroups;
    let costGroups = sourceCostGroups.map(g => ({
      ...g,
      lineItems: (g.costItems || g.lineItems || []).filter(item => matchesCostTypes(item, effectiveCostTypes))
    })).filter(g => g.lineItems.length > 0);

    const sortKey = (g) => {
      const gp = g.parentCostGroup?.parentCostGroup?.name || '';
      const p = g.parentCostGroup?.name || '';
      const own = g.name || '';
      if (gp) return [gp, p, own];
      if (p) return [p, own, ''];
      return [own, '', ''];
    };
    const compareTuples = (a, b) => {
      for (let i = 0; i < 3; i++) {
        const cmp = a[i].localeCompare(b[i], undefined, { numeric: true, sensitivity: 'base' });
        if (cmp !== 0) return cmp;
      }
      return 0;
    };
    const sortByTree = (arr) => [...arr].sort((a, b) => compareTuples(sortKey(a), sortKey(b)));

    if (!searchQuery) {
      return sortByTree(costGroups);
    }

    const filtered = costGroups.map(group => {
      const groupName = (cleanCostGroupName(group.name) || '').toLowerCase();
      const parentName = (cleanCostGroupName(group.parentCostGroup?.name) || '').toLowerCase();
      const grandparentName = (cleanCostGroupName(group.parentCostGroup?.parentCostGroup?.name) || '').toLowerCase();
      const groupHierarchyMatches =
        groupName.includes(lowerQuery) ||
        parentName.includes(lowerQuery) ||
        grandparentName.includes(lowerQuery);

      if (groupHierarchyMatches) {
        return { ...group, lineItems: group.lineItems || [] };
      } else {
        const matchingItems = (group.lineItems || []).filter(item => {
          const itemName = item.name?.toLowerCase() || '';
          const costCodeNumber = item.costCode?.number?.toString().toLowerCase() || '';
          const costCodeName = item.costCode?.name?.toLowerCase() || '';
          return itemName.includes(lowerQuery)
            || costCodeNumber.includes(lowerQuery)
            || costCodeName.includes(lowerQuery);
        });
        return { ...group, lineItems: matchingItems };
      }
    }).filter(group => group.lineItems.length > 0);

    return sortByTree(filtered);
  }

  // --- Assignment filtering (Cost Code lens) ---

  function getFilteredAssignmentItemsByCode(lineIndex) {
    const searchQuery = (assignmentSearchQueries[lineIndex] || '').trim();
    const lowerQuery = searchQuery.toLowerCase();

    let sourceCostGroups = isJobMode ? jobCostGroups : poCostGroups;
    let costGroups = sourceCostGroups.map(g => ({
      ...g,
      lineItems: (g.costItems || g.lineItems || []).filter(item => matchesCostTypes(item, effectiveCostTypes))
    }));

    const flat = [];
    costGroups.forEach(group => {
      const originalGroupIndex = sourceCostGroups.findIndex(g => g.id === group.id);
      (group.lineItems || []).forEach(item => {
        flat.push({ item, originalGroupIndex, costGroup: group });
      });
    });

    const filtered = !searchQuery ? flat : flat.filter(({ item }) => {
      const itemName = item.name?.toLowerCase() || '';
      const codeNum = item.costCode?.number?.toString().toLowerCase() || '';
      const codeName = item.costCode?.name?.toLowerCase() || '';
      const parentCodeNum = item.costCode?.parentCostCode?.number?.toString().toLowerCase() || '';
      const parentCodeName = item.costCode?.parentCostCode?.name?.toLowerCase() || '';
      return itemName.includes(lowerQuery)
        || codeNum.includes(lowerQuery)
        || codeName.includes(lowerQuery)
        || parentCodeNum.includes(lowerQuery)
        || parentCodeName.includes(lowerQuery);
    });

    const bucketsByParent = new Map();
    const NO_CODE_KEY = '__no_code__';
    for (const entry of filtered) {
      const parent = entry.item.costCode?.parentCostCode;
      const key = parent?.id || (entry.item.costCode?.id || NO_CODE_KEY);
      if (!bucketsByParent.has(key)) {
        bucketsByParent.set(key, {
          id: key,
          name: parent
            ? `${parent.number || ''}  ${parent.name || ''}`.trim()
            : (entry.item.costCode
                ? `${entry.item.costCode.number || ''}  ${entry.item.costCode.name || ''}`.trim()
                : 'Uncategorised'),
          parentCostGroup: null,
          lineItems: []
        });
      }
      const bucket = bucketsByParent.get(key);
      bucket.lineItems.push({
        ...entry.item,
        _originalGroupIndex: entry.originalGroupIndex,
        costGroup: entry.costGroup
          ? {
              id: entry.costGroup.id,
              name: entry.costGroup.name,
              parentCostGroup: entry.costGroup.parentCostGroup || null
            }
          : null
      });
    }

    return Array.from(bucketsByParent.values()).sort((a, b) => {
      const ax = parseInt((a.name.match(/^\d+/) || ['9999'])[0], 10);
      const bx = parseInt((b.name.match(/^\d+/) || ['9999'])[0], 10);
      return ax - bx;
    });
  }

  // The extra args are unused here; passing them from the template makes
  // Svelte re-run the lookup when the search, lens or type filter changes.
  function getAssignmentBucketsByActiveLens(lineIndex, _searchQuery, _lens, _types) {
    return activeLens === 'costCode'
      ? getFilteredAssignmentItemsByCode(lineIndex)
      : getFilteredAssignmentItems(lineIndex);
  }

  function handleInputFocusOut(type) {
    setTimeout(() => {
      if (type === 'customer') showCustomerDropdown = false;
      else if (type === 'vendorOrder') showVendorOrderDropdown = false;
      else if (type === 'vendor') showVendorDropdown = false;
    }, 200);
  }

  // --- Vendor Matching ---

  async function searchVendors() {
    isLoadingVendors = true;
    try {
      const response = await fetch('/api/jt/vendors');
      if (response.ok) {
        const data = await response.json();
        jtVendors = data.vendors || data || [];
        filteredVendors = jtVendors;
      }
    } catch (err) {
      console.error('Error searching vendors:', err);
    } finally {
      isLoadingVendors = false;
    }
  }

  // normalizeForComparison and findVendorCandidates are imported from ../lib/vendorMatch.js

  async function loadVendorAliases() {
    if (vendorAliasesLoaded) return;
    try {
      const response = await fetch('/api/users/vendor-aliases');
      if (response.ok) {
        const data = await response.json();
        vendorAliases = data.vendorAliases || {};
      }
    } catch (err) {
      console.warn('Could not load vendor aliases:', err.message);
    }
    vendorAliasesLoaded = true;
  }

  async function confirmVendorCandidate(vendor, sourceName) {
    selectedVendorId = vendor.id;
    vendorSearchQuery = vendor.name;
    autoMatchedVendor = true;
    pendingVendorCandidates = [];

    try {
      await fetch('/api/users/vendor-aliases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceName, jtVendorId: vendor.id })
      });
      const key = normalizeForComparison(sourceName);
      vendorAliases[key] = vendor.id;
      console.log(`💾 Saved vendor alias: "${sourceName}" -> "${vendor.name}"`);
    } catch (err) {
      console.warn('Failed to save vendor alias:', err.message);
    }
  }

  function rejectVendorCandidates() {
    pendingVendorCandidates = [];
    autoMatchFailed = true;
  }

  async function clearVendorMatch() {
    const sourceName = extractedData.vendorName;
    const aliasKey = sourceName ? normalizeForComparison(sourceName) : null;

    selectedVendorId = null;
    autoMatchedVendor = false;
    pendingVendorCandidates = [];
    autoMatchFailed = true;
    vendorSearchQuery = sourceName || '';
    showVendorDropdown = false;
    filterVendorList();
    requiredVendorFields = [];
    requiredVendorFieldValues = {};
    requiredVendorError = '';

    if (aliasKey && vendorAliases[aliasKey]) {
      delete vendorAliases[aliasKey];
      vendorAliases = { ...vendorAliases };
      try {
        await fetch('/api/users/vendor-aliases', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sourceName })
        });
        console.log(`🗑️ Cleared vendor alias for "${sourceName}"`);
      } catch (err) {
        console.warn('Failed to delete vendor alias:', err.message);
      }
    }
  }

  // Ticking "send overhead to Xero only" has no job or PO, and those were the
  // only things that ran the supplier match. A bill that opens already marked
  // overhead is matched in startAutoReviewForCurrentBill. This handles the tick
  // itself. It must not be a reactive on the bill id: that id changes before
  // loadBillData clears the previous bill, so the match would use the previous
  // supplier and could write it onto the next bill.
  function onAccountingOnlyChange(event) {
    if (!event.currentTarget.checked || isQuickBooks || readOnly || selectedVendorId || isAutoMatchingVendor) return;
    autoMatchVendor();
  }

  async function autoMatchVendor() {
    const uploadId = currentBillUploadId;
    const stillThisBill = () => uploadId === currentBillUploadId;
    autoMatchedVendor = false;
    autoMatchFailed = false;
    pendingVendorCandidates = [];
    isAutoMatchingVendor = true;

    if (jtVendors.length === 0) await searchVendors();
    if (!stillThisBill()) return;
    await loadVendorAliases();
    if (!stillThisBill()) return;

    const vendorName = extractedData.vendorName;
    if (!vendorName || jtVendors.length === 0) {
      autoMatchFailed = true;
      isAutoMatchingVendor = false;
      return;
    }

    await new Promise(r => setTimeout(r, 300));
    if (!stillThisBill()) return;

    const { exactMatch, candidates } = findVendorCandidates(vendorName, jtVendors, vendorAliases);

    if (exactMatch) {
      console.log(`✅ Auto-matched: "${vendorName}" -> "${exactMatch.name}"`);
      selectedVendorId = exactMatch.id;
      autoMatchedVendor = true;
      vendorSearchQuery = exactMatch.name;
      isAutoMatchingVendor = false;
      return;
    }

    if (candidates.length > 0) {
      pendingVendorCandidates = candidates;
      pendingVendorSourceName = vendorName;
      console.log(`🔍 Found ${candidates.length} candidate(s) for "${vendorName}":`, candidates.map(v => v.name));
    } else {
      autoMatchFailed = true;
    }

    isAutoMatchingVendor = false;
  }

  function handleVendorSearch(event) {
    vendorSearchQuery = event.target.value;
    autoMatchedVendor = false;
    filterVendorList();
  }

  function filterVendorList() {
    if (!vendorSearchQuery || vendorSearchQuery.trim() === '') {
      filteredVendors = jtVendors;
    } else {
      const query = vendorSearchQuery.toLowerCase().trim();
      filteredVendors = jtVendors.filter(v => v.name.toLowerCase().includes(query));
    }
  }

  function selectVendor(vendorId) {
    selectedVendorId = vendorId;
    const v = jtVendors.find(x => x.id === vendorId);
    if (v) vendorSearchQuery = v.name;
    autoMatchedVendor = true;
    autoMatchFailed = false;
    showVendorDropdown = false;
    requiredVendorFields = [];
    requiredVendorFieldValues = {};
    requiredVendorError = '';
  }

  async function createNewVendor() {
    const vendorName = (extractedData.vendorName || vendorSearchQuery || '').trim();
    if (!vendorName) {
      error = 'A vendor name is required to create a vendor in JobTread';
      return;
    }
    if (!requiredCustomFieldsFilled(requiredVendorFields, requiredVendorFieldValues)) {
      requiredVendorError = requiredVendorError || 'Choose the required JobTread field, then create the vendor.';
      return;
    }
    isCreatingVendor = true;
    error = null;
    try {
      const customFieldValues = customFieldValuesFromSelections(requiredVendorFieldValues);
      const response = await fetch('/api/jt/createvendor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: vendorName,
          ...(Object.keys(customFieldValues).length ? { customFieldValues } : {})
        })
      });
      const result = await response.json().catch(() => ({}));
      if (isRequiredCustomFieldResult(result)) {
        requiredVendorFields = mergeRequiredCustomFields(requiredVendorFields, result.requiredCustomFields);
        requiredVendorError = result.error;
        return;
      }
      if (!response.ok) {
        throw new Error(result.error || 'Failed to create vendor');
      }
      const created = parseCreatedVendor(result, vendorName);
      if (!created) {
        throw new Error('JobTread did not return a vendor ID');
      }
      if (!jtVendors.some(v => v.id === created.id)) {
        jtVendors = [...jtVendors, created];
      }
      selectedVendorId = created.id;
      vendorSearchQuery = created.name;
      autoMatchedVendor = true;
      autoMatchFailed = false;
      pendingVendorCandidates = [];
      requiredVendorFields = [];
      requiredVendorFieldValues = {};
      requiredVendorError = '';
    } catch (err) {
      console.error('Error creating vendor:', err);
      error = 'Failed to create vendor: ' + err.message;
    } finally {
      isCreatingVendor = false;
    }
  }

  // --- Sync orchestration ---

  function resolveAccountCode(costItemId) {
    const allItems = getAllCostItems();
    const item = allItems.find(i => i.id === costItemId);
    if (item?.costCode?.number) {
      const mapped = costCodeMappings[item.costCode.number];
      if (mapped) return mapped;
      if (item.costCode.parentCostCode?.number) {
        const parentMapped = costCodeMappings[item.costCode.parentCostCode.number];
        if (parentMapped) return parentMapped;
      }
    }
    return userExpenseCode || null;
  }

  // Per-line Xero account codes for the sync. For an overhead bill with a chosen
  // expense account, every line uses that account; otherwise resolve from the
  // assigned cost item, falling back to the org default expense code.
  function buildLineItemAccountCodes() {
    if (accountingOnly && overheadAccountCode) {
      return (extractedData.lineItems || []).map(() => overheadAccountCode);
    }
    // Whole-bill split: Xero keeps the supplier's own lines, all coded to the
    // account of the budget item taking the biggest share.
    if (!accountingOnly && splitMode && splitResult.lines.length > 0) {
      const lead = splitResult.lines.reduce((a, b) => (b.amount > a.amount ? b : a));
      const code = resolveAccountCode(lead.item.id) || userExpenseCode || null;
      return (extractedData.lineItems || []).map(() => code);
    }
    return (extractedData.lineItems || []).map((_, i) => {
      const costItemId = lineItemAssignments[i];
      return costItemId ? resolveAccountCode(costItemId) : null;
    }).map(code => code || userExpenseCode || null);
  }

  // The server resolves tracking from the org owner's settings. Overhead bills
  // have no job, so they send no job context and get no tracking.
  function jobContextForSync() {
    if (accountingOnly) return null;
    const source = matchedJob || matchedPO;
    if (!source || (!source.jobId && !source.jobName && !source.jobNumber)) return null;
    return {
      jobId: source.jobId || null,
      jobName: source.jobName || '',
      jobNumber: source.jobNumber != null && source.jobNumber !== '' ? String(source.jobNumber) : ''
    };
  }

  function buildJtPayload(xeroBillId, isCreditNote, xeroCurrency = null, xeroCurrencyRate = null, markPaid = markAsPaid) {
    return buildDirectBillJtPayload({
      extractedData,
      lineItemAssignments,
      costItems: getAllCostItems(),
      isJobMode,
      matchedJob,
      matchedPO,
      selectedVendorId,
      markAsPaid: markPaid,
      upload: currentBill,
      xeroBillId,
      isCreditNote,
      xeroCurrency,
      xeroCurrencyRate,
      caProvince,
      country: $user?.country,
      splitMode,
      splitTarget,
      splitCostTypes: effectiveCostTypes,
      costGroups: activeCostGroups
    });
  }

  // Posts one bill to Xero. If Xero already has a live bill from this supplier
  // with this invoice number (keyed in there, Hubdoc/Dext, or this PDF uploaded
  // twice), the server refuses and Sync Results offers "Use the bill in Xero"
  // or "Create anyway" (allowDuplicate).
  async function postDirectBillToXero(uploadId, enrichment, allowDuplicate = false) {
    const xeroResponse = await fetch('/api/direct-bills/sync-to-xero', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uploadIds: [uploadId],
        enrichment,
        ...(allowDuplicate ? { allowDuplicateFor: [uploadId] } : {})
      })
    });
    const xeroData = await xeroResponse.json();
    return { xeroResponse, xeroData };
  }

  // markPaid: null = the "Mark as Paid" tickbox; true/false overrides it (a
  // linked Xero bill's own paid status).
  async function syncToJobTread(xeroResult, markPaid = null, options = {}) {
    const jtPayload = buildJtPayload(
      xeroResult.xeroBill.id,
      xeroResult.xeroBill.isCreditNote,
      xeroResult.xeroBill.currency,
      xeroResult.xeroBill.currencyRate,
      markPaid ?? markAsPaid
    );
    if (options.enforceSupplierDuplicateCheck) jtPayload.enforceSupplierDuplicateCheck = true;

    const jtResponse = await fetch('/api/jt/createvendorbill', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(jtPayload)
    });

    const jtData = await jtResponse.json();

    if (jtResponse.ok) {
      if (jtData.createDocument) {
        const doc = jtData.createDocument.createdDocument;
        return {
          success: true,
          documentId: doc?.id,
          jobId: doc?.job?.id,
          url: doc?.job?.id && doc?.id ? `https://app.jobtread.com/jobs/${doc.job.id}/documents/${doc.id}` : null,
          pdfAttachedToJT: jtData.pdfAttachedToJT || false,
          pdfAttachmentError: jtData.pdfAttachmentError || null
        };
      } else if (jtData.results) {
        return jtData.results[0];
      }
      return { success: false, error: 'Unexpected response' };
    }
    return {
      success: false,
      error: jtData.error || 'Failed to create JobTread vendor bill',
      alreadySynced: jtData.alreadySynced === true,
      duplicateSupplierInvoice: jtData.duplicateSupplierInvoice === true
    };
  }

  // The Xero bill checked out, but the upload is not synced yet. Keep the
  // duplicate actions so a JobTread failure can be retried.
  function pendingXeroLink(xeroBillId, xeroBill, message) {
    return {
      success: false,
      pendingLink: true,
      duplicateSupplierInvoice: true,
      error: message || 'Xero was left as it is.',
      existing: {
        id: xeroBillId,
        count: 1,
        status: xeroBill?.status || null
      },
      xeroBill: xeroBill || null
    };
  }

  async function requestExistingXeroLink(uploadId, xeroBillId, extra) {
    const res = await fetch(`/api/direct-bills/${uploadId}/link-existing-xero`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ xeroBillId, ...extra })
    });
    const data = await res.json();
    return { res, data, xeroResult: data?.results?.[0] };
  }

  // opts.allowDuplicate: the user chose "Create anyway" after Xero already had
  // this supplier invoice. (on:click passes an Event, which has no such field.)
  async function syncBoth(opts) {
    if (syncLocked) return;
    const allowDuplicate = opts?.allowDuplicate === true;
    const uploadId = currentBill?.uploadId;
    const validation = validateBill(extractedData);
    if (!validation.valid) { error = validation.error; return; }

    syncing = true;
    error = null;
    clearedSyncedNote = false;
    await setSyncResults([], uploadId);
    syncProgress = { current: 0, total: 2, step: 'Saving...' };

    try {
      await saveCurrentBillData();

      // QuickBooks + overhead: push straight to QuickBooks over BZ's own
      // connection and skip JobTread entirely. A job bill never comes down this
      // path — JobTread syncs those to QuickBooks itself, and sending both would
      // double-post.
      if (isQuickBooks && accountingOnly) {
        syncProgress = { current: 1, total: 1, step: 'Syncing to QuickBooks...' };

        const qboEnrichment = {};
        qboEnrichment[uploadId] = {
          ...(overheadAccountCode && { overheadAccountId: overheadAccountCode }),
          // An explicitly chosen QuickBooks vendor wins over name matching on
          // the server, so picking one here is authoritative.
          ...(selectedQboVendorId && { qboVendorId: selectedQboVendorId, qboVendorName: qboVendorQuery }),
          // Already paid → the server posts a Purchase against this account
          // instead of a Bill, so it never lands in Accounts Payable.
          alreadyPaid: markAsPaid === true,
          ...(qboPaymentAccountId && { paymentAccountId: qboPaymentAccountId })
        };

        const qboResponse = await fetch('/api/qbo/sync-overhead', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uploadIds: [uploadId], enrichment: qboEnrichment })
        });
        const qboData = await qboResponse.json();

        // A top-level failure (not connected, no default account) has no
        // per-bill result to show — surface it as the page error instead.
        if (!qboData.results) {
          error = qboData.error || 'Failed to sync to QuickBooks';
          return;
        }

        await setSyncResults([{
          uploadId,
          xero: null,
          jobtread: null,
          accountingOnly: true,
          quickbooks: qboData.results[0]
        }], uploadId);
        return;
      }

      // QuickBooks users run JobTread-only: skip the Xero bill entirely and push
      // straight to JobTread (JobTread's native QuickBooks integration syncs it on).
      if (isQuickBooks) {
        syncProgress = { current: 1, total: 1, step: 'Syncing to JobTread...' };
        const jtResult = await syncToJobTread({ xeroBill: { id: null, isCreditNote: extractedData.isCreditNote || false } });
        await setSyncResults([{
          uploadId,
          xero: null,
          jobtread: jtResult
        }], uploadId);
        return;
      }

      // Step 1: Push to Xero
      syncProgress = { current: 1, total: 2, step: 'Syncing to Xero...' };

      const lineItemAccountCodes = buildLineItemAccountCodes();

      const job = jobContextForSync();
      const reference = extractedData.poNumber || extractedData.invoiceNumber || null;

      const enrichment = {};
      enrichment[uploadId] = {
        lineItemAccountCodes,
        ...(xeroContactChoice && { xeroContact: xeroContactChoice }),
        markAsPaid: markAsPaid === true,
        ...(job && { job }),
        ...(reference && { reference })
      };

      const { xeroResponse, xeroData } = await postDirectBillToXero(uploadId, enrichment, allowDuplicate);
      if (!xeroResponse.ok || !xeroData.success) {
        await setSyncResults([{
          uploadId,
          xero: { success: false, error: xeroData.error || 'Failed to sync to Xero' },
          jobtread: null
        }], uploadId);
        return;
      }

      const xeroResult = xeroData.results[0];
      if (xeroResult?.needsXeroContact) {
        xeroContact = null;
        xeroContactCandidates = xeroResult.candidates || xeroContactCandidates;
      }
      if (!xeroResult.success) {
        await setSyncResults([{
          uploadId,
          xero: xeroResult,
          jobtread: null
        }], uploadId);
        return;
      }

      // Accounting-only (overhead): Xero only — do NOT push to JobTread.
      if (accountingOnly) {
        await setSyncResults([{
          uploadId,
          xero: xeroResult,
          jobtread: null,
          accountingOnly: true
        }], uploadId);
        return;
      }

      // Step 2: Push to JobTread
      syncProgress = { current: 2, total: 2, step: 'Syncing to JobTread...' };
      const jtResult = await syncToJobTread(xeroResult);

      await setSyncResults([{
        uploadId,
        xero: xeroResult,
        jobtread: jtResult
      }], uploadId);

    } catch (err) {
      console.error('Error in combined sync:', err);
      error = err.message;
    } finally {
      syncing = false;
    }
  }

  // The supplier invoice is already in Xero: confirm that bill (nothing changes
  // in Xero) and create the JobTread bill against it. The upload is marked
  // synced only after JobTread accepts it, or immediately in overhead mode.
  // If the Xero bill is already paid, the JobTread bill is marked paid too —
  // no Xero webhook will come for a payment that happened before the link.
  async function linkToExistingXeroBill(xeroBillId) {
    if (syncing || syncLocked || !xeroBillId) return;
    const uploadId = currentBill?.uploadId;
    const validation = validateBill(extractedData);
    if (!validation.valid) { error = validation.error; return; }

    syncing = true;
    error = null;
    syncProgress = { current: 1, total: accountingOnly ? 1 : 2, step: 'Linking to the bill in Xero...' };
    try {
      await saveCurrentBillData();
      const preview = await requestExistingXeroLink(uploadId, xeroBillId, {
        accountingOnly: accountingOnly === true
      });
      if (!preview.res.ok || !preview.xeroResult?.success) {
        await setSyncResults([{
          uploadId,
          xero: { success: false, error: preview.data?.error || 'Could not link to the bill in Xero' },
          jobtread: null
        }], uploadId);
        return;
      }

      if (accountingOnly) {
        await setSyncResults([{ uploadId, xero: preview.xeroResult, jobtread: null, accountingOnly: true }], uploadId);
        return;
      }

      syncProgress = { current: 2, total: 2, step: 'Syncing to JobTread...' };
      const jtResult = await syncToJobTread(
        preview.xeroResult,
        preview.xeroResult.xeroBill.status === 'PAID',
        { enforceSupplierDuplicateCheck: true }
      );
      // alreadySynced: a previous attempt created the JobTread bill and the
      // client never got to mark this upload synced. Finish that link.
      const jtDone = jtResult.success || jtResult.alreadySynced === true;
      if (!jtDone) {
        await setSyncResults([{
          uploadId,
          xero: pendingXeroLink(xeroBillId, preview.xeroResult.xeroBill, jtResult.error),
          jobtread: jtResult
        }], uploadId);
        return;
      }

      const committed = await requestExistingXeroLink(uploadId, xeroBillId, { commit: true });
      if (!committed.res.ok || !committed.xeroResult?.success) {
        await setSyncResults([{
          uploadId,
          xero: pendingXeroLink(
            xeroBillId,
            preview.xeroResult.xeroBill,
            committed.data?.error || 'The JobTread bill was created, but this upload was not marked synced. Try again.'
          ),
          jobtread: jtResult.success ? jtResult : { success: true, alreadySynced: true }
        }], uploadId);
        return;
      }

      await setSyncResults([{
        uploadId,
        xero: committed.xeroResult,
        jobtread: jtResult.success ? jtResult : { success: true, alreadySynced: true }
      }], uploadId);
    } catch (err) {
      console.error('Error linking to existing Xero bill:', err);
      error = err.message;
    } finally {
      syncing = false;
    }
  }

  let submittingForApproval = false;
  // Shown when Xero already has this supplier invoice. The browser confirm
  // cannot be styled, so the choice stays in the page.
  let duplicatePrompt = null;

  async function finishSubmitForApproval(reviewPayload, allowDuplicate) {
    const response = await fetch(`/api/direct-bills/${currentBill.uploadId}/submit-for-approval`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reviewPayload, ...(allowDuplicate ? { allowDuplicate: true } : {}) })
    });
    const data = await response.json();
    if (response.ok && data.duplicateSupplierInvoice && !allowDuplicate) {
      duplicatePrompt = { message: data.error, reviewPayload };
      return;
    }
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to submit for approval');
    }
    navigate('/direct-bill-import', {
      state: { approvalNotice: { id: Date.now(), text: 'Submitted for approval step successfully' } }
    });
  }

  async function confirmDuplicateSubmit() {
    const payload = duplicatePrompt?.reviewPayload;
    duplicatePrompt = null;
    if (!payload) return;
    submittingForApproval = true;
    error = null;
    try {
      await finishSubmitForApproval(payload, true);
    } catch (err) {
      console.error('Error submitting for approval:', err);
      error = err.message;
    } finally {
      submittingForApproval = false;
    }
  }

  function snapshotLineAllocations() {
    if (splitMode && splitTarget) {
      const label = splitTarget.label || 'Split';
      return (extractedData.lineItems || []).map((line) => ({
        description: line.description || '',
        amount: Number(line.amount) || 0,
        costItemName: null,
        costGroupName: `Split across ${label}`
      }));
    }
    const items = getAllCostItems();
    return (extractedData.lineItems || []).map((line, i) => {
      const costItem = items.find(it => it.id === lineItemAssignments[i]);
      return {
        description: line.description || '',
        amount: Number(line.amount) || 0,
        costItemName: costItem?.name || null,
        costGroupName: costItem?.costGroupName ? cleanCostGroupName(costItem.costGroupName) : null
      };
    });
  }

  async function submitBillForApproval() {
    const validation = validateBill(extractedData);
    if (!validation.valid) { error = validation.error; return; }

    submittingForApproval = true;
    error = null;

    try {
      await saveCurrentBillData();

      const lineItemAccountCodes = buildLineItemAccountCodes();

      const job = jobContextForSync();
      const reference = extractedData.poNumber || extractedData.invoiceNumber || null;

      const enrichment = {};
      enrichment[currentBill.uploadId] = {
        lineItemAccountCodes,
        ...(xeroContactChoice && { xeroContact: xeroContactChoice }),
        markAsPaid: markAsPaid === true,
        ...(job && { job }),
        ...(reference && { reference })
      };

      const reviewPayload = {
        selectedVendorId,
        isJobMode,
        accountingOnly,
        overheadAccountCode,
        markAsPaid: markAsPaid === true,
        ...(xeroContactChoice && { xeroContact: xeroContactChoice }),
        selectedQboVendorId: selectedQboVendorId || null,
        qboVendorName: qboVendorQuery || null,
        qboPaymentAccountId: qboPaymentAccountId || null,
        matchedJob: matchedJob ? { jobId: matchedJob.jobId, jobName: matchedJob.jobName, jobNumber: matchedJob.jobNumber, customerId: matchedJob.customerId } : null,
        matchedPO: matchedPO ? { vendorOrderId: matchedPO.vendorOrderId, vendorOrderNumber: matchedPO.vendorOrderNumber, jobId: matchedPO.jobId, jobName: matchedPO.jobName, jobNumber: matchedPO.jobNumber || '' } : null,
        lineItemAssignments: { ...lineItemAssignments },
        lineAllocations: snapshotLineAllocations(),
        splitMode,
        splitTarget,
        splitCostTypes: effectiveCostTypes,
        extractedData: { ...extractedData },
        enrichment,
        activeLens
      };

      await finishSubmitForApproval(reviewPayload, false);
    } catch (err) {
      console.error('Error submitting for approval:', err);
      error = err.message;
    } finally {
      submittingForApproval = false;
    }
  }

  // The page load and the signed-in user both carry the org flag. Either
  // source is enough, so the label follows the setting even if one response
  // arrived before the other.
  $: requireApproval = billSettings.requireDirectBillApproval === true
    || $user?.billSettings?.requireDirectBillApproval === true;
  $: billIsAwaitingApproval = currentBill?.status === 'awaiting_approval';
  $: showSubmitForApproval = requireApproval && currentBill?.status === 'completed';

  async function retryXeroWithDefaultCode() {
    const uploadId = currentBill?.uploadId;
    syncing = true;
    error = null;
    await setSyncResults([], uploadId);
    syncProgress = { current: 1, total: 2, step: 'Retrying Xero with default expense code...' };

    try {
      const lineItemAccountCodes = (extractedData.lineItems || []).map(() => userExpenseCode || null);
      const job = jobContextForSync();
      const reference = extractedData.poNumber || extractedData.invoiceNumber || null;

      const enrichment = {};
      enrichment[uploadId] = {
        lineItemAccountCodes,
        ...(xeroContactChoice && { xeroContact: xeroContactChoice }),
        markAsPaid: markAsPaid === true,
        ...(job && { job }),
        ...(reference && { reference })
      };

      const { xeroResponse, xeroData } = await postDirectBillToXero(uploadId, enrichment);
      if (!xeroResponse.ok || !xeroData.success) {
        await setSyncResults([{
          uploadId,
          xero: { success: false, error: xeroData.error || 'Retry failed' },
          jobtread: null
        }], uploadId);
        return;
      }

      const xeroResult = xeroData.results[0];
      if (xeroResult?.needsXeroContact) {
        xeroContact = null;
        xeroContactCandidates = xeroResult.candidates || xeroContactCandidates;
      }
      if (!xeroResult.success) {
        await setSyncResults([{
          uploadId,
          xero: xeroResult,
          jobtread: null
        }], uploadId);
        return;
      }

      // Xero succeeded on retry, now push to JobTread
      syncProgress = { current: 2, total: 2, step: 'Syncing to JobTread...' };
      const jtResult = await syncToJobTread(xeroResult);

      await setSyncResults([{
        uploadId,
        xero: xeroResult,
        jobtread: jtResult
      }], uploadId);

    } catch (err) {
      console.error('Error in retry sync:', err);
      error = err.message;
    } finally {
      syncing = false;
    }
  }
</script>

<Layout activeRoute="direct-bill-import">
  <div class="max-w-[2200px] mx-auto px-4 py-6">
    <div class="mb-4 flex items-center justify-between">
      <div class="flex items-center gap-3">
        <h1 class="text-xl font-bold">{readOnly ? 'View Synced Bill' : 'Review & Sync Bills'}</h1>
        {#if readOnly}
          <span class="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-600 rounded">Read Only</span>
        {/if}
      </div>
      <button on:click={goBack} class="text-gray-600 hover:text-gray-900 text-sm">
        ← Back to Upload
      </button>
    </div>

    {#if error}
      <div class="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">{error}</div>
    {/if}

    {#if syncResults.length > 0 && !currentBill}
      <SyncResults
        {syncResults}
        {selectedBills}
        {overheadDest}
        {userExpenseCode}
        {syncing}
        {accountingOnly}
        onRetry={retryXeroWithDefaultCode}
        onLinkExisting={linkToExistingXeroBill}
        onCreateAnyway={() => syncBoth({ allowDuplicate: true })}
        onClearSynced={clearSyncedStatus}
        {clearingSynced}
        onBack={goBack}
      />
    {:else if currentBill}
      <!-- Navigation between bills -->
      {#if selectedBills.length > 1}
        <div class="mb-3 flex items-center justify-between bg-white rounded-lg shadow p-3">
          <button on:click={prevBill} disabled={currentBillIndex === 0 || syncing} class="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-[#f0f4ff] disabled:opacity-50 disabled:cursor-not-allowed">← Previous</button>
          <span class="text-sm text-gray-700">Bill {currentBillIndex + 1} of {selectedBills.length}</span>
          <button on:click={nextBill} disabled={currentBillIndex === selectedBills.length - 1 || syncing} class="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-[#f0f4ff] disabled:opacity-50 disabled:cursor-not-allowed">Next →</button>
        </div>
      {/if}

      <!-- 3-column layout -->
      <div class="bg-white rounded-lg shadow overflow-hidden">
        <div class="grid grid-cols-1 lg:grid-cols-12 lg:grid-rows-[minmax(0,1fr)] lg:h-[calc(100vh-210px)] lg:min-h-[550px] gap-0">
          <!-- PDF Preview (5/12 = ~42%) — last on mobile -->
          <div class="order-3 lg:order-none lg:col-span-5 lg:border-r border-gray-200 bg-gray-50 lg:flex lg:flex-col lg:min-h-0">
            <div class="p-3 border-b border-gray-200 bg-white">
              <div class="flex items-center justify-between">
                <div>
                  <h2 class="text-sm font-semibold">PDF Preview</h2>
                  <p class="text-xs text-gray-500">{currentBill.fileName}</p>
                </div>
                <div class="flex items-center gap-1">
                  <button on:click={() => pdfZoom = Math.max(50, pdfZoom - 10)} class="px-1.5 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-100" title="Zoom Out">−</button>
                  <span class="text-xs font-medium min-w-[36px] text-center">{pdfZoom}%</span>
                  <button on:click={() => pdfZoom = Math.min(200, pdfZoom + 10)} class="px-1.5 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-100" title="Zoom In">+</button>
                  <button on:click={() => pdfZoom = 100} class="px-1.5 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-100" title="Reset">Reset</button>
                </div>
              </div>
            </div>
            <div class="p-2 lg:overflow-auto lg:flex-1 lg:min-h-0 {currentBill.mimeType && currentBill.mimeType.startsWith('image/') ? '' : 'h-[75vh] overflow-auto'}">
              {#if currentBill.presignedUrl}
                {#if currentBill.mimeType && currentBill.mimeType.startsWith('image/')}
                  <!-- Photo (mobile camera capture): render natively; the PDF viewer can't display images. -->
                  <img src={currentBill.presignedUrl} alt="Uploaded bill" class="border border-gray-300 rounded" style="width: {pdfZoom}%; height: auto;" />
                {:else}
                  <div style="transform: scale({pdfZoom / 100}); transform-origin: top left; width: {100 / (pdfZoom / 100)}%; height: {100 / (pdfZoom / 100)}%;">
                    <iframe src="{currentBill.presignedUrl}#toolbar=0&navpanes=0&scrollbar=1&zoom={pdfZoom}" class="w-full h-full border border-gray-300 rounded" title="PDF Preview"></iframe>
                  </div>
                {/if}
              {:else}
                <div class="flex items-center justify-center h-full text-gray-500 text-sm">Loading file...</div>
              {/if}
            </div>
          </div>

          <!-- Extracted Data + Line Item Assignments (4/12 = ~33%) — second on mobile -->
          <div bind:this={extractedColEl} class="order-2 lg:order-none lg:col-span-4 lg:border-r border-gray-200 p-4 lg:overflow-y-auto lg:min-h-0 {matchingComplete && !currentBillSynced ? '' : 'bg-gray-50'}">
            <div class="flex items-center gap-2 mb-3">
              <h2 class="text-sm font-semibold {matchingComplete && !currentBillSynced ? '' : 'text-gray-400'}">Extracted Data</h2>
              {#if extractedData?.isCreditNote}
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">Credit Note</span>
              {/if}
            </div>

            {#if noBudgetLoaded}
              <div class="p-3 mb-3 bg-amber-50 border-2 border-amber-400 rounded-md">
                <p class="text-sm font-medium text-amber-800">No budget lines found for this {isJobMode ? 'job' : 'PO'}</p>
                <p class="text-xs text-amber-700 mt-1">
                  JobTread returned no cost groups to assign against. Check the job has budget cost items
                  (not only PO/bill lines), try Refresh, or switch mode.
                </p>
              </div>
            {:else if !matchingComplete && !isJobMode && selectedCustomer && !selectedVendorOrder}
              <div class="p-3 mb-3 bg-orange-50 border-2 border-orange-300 rounded-md text-center">
                <p class="text-sm font-medium text-orange-700">Select a vendor order →</p>
                <p class="text-xs text-orange-600 mt-0.5">Choose a PO in the matching panel, or switch to Job mode.</p>
              </div>
            {:else if !matchingComplete}
              <div class="p-3 mb-3 bg-orange-50 border-2 border-orange-300 rounded-md text-center">
                <p class="text-sm font-medium text-orange-700">Select a Job first →</p>
                <p class="text-xs text-orange-600 mt-0.5">Use the matching panel to select a job{!isJobMode ? ' and PO' : ''}</p>
              </div>
            {/if}
            
            <div class="space-y-3 {matchingComplete && !currentBillSynced ? '' : 'opacity-50 pointer-events-none'}">
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">Vendor Name *</label>
                <input type="text" bind:value={extractedData.vendorName} readonly={!matchingComplete} class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 {!matchingComplete ? 'bg-gray-100 cursor-not-allowed' : ''}" />
              </div>

              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="block text-xs font-medium text-gray-700 mb-1">Invoice #</label>
                  <input type="text" bind:value={extractedData.invoiceNumber} readonly={!matchingComplete} class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 {!matchingComplete ? 'bg-gray-100 cursor-not-allowed' : ''}" />
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-700 mb-1">PO / Reference</label>
                  <input type="text" bind:value={extractedData.poNumber} readonly={!matchingComplete} class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 {!matchingComplete ? 'bg-gray-100 cursor-not-allowed' : ''}" placeholder="PO number" />
                </div>
              </div>

              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="block text-xs font-medium text-gray-700 mb-1">Issue Date</label>
                  <input type="date" value={extractedData.issueDate || ''} on:input={(e) => setExtractedDate('issueDate', e.currentTarget.value)} readonly={!matchingComplete} class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 {!matchingComplete ? 'bg-gray-100 cursor-not-allowed' : !extractedData.issueDate ? 'bg-orange-200 border-orange-400' : ''}" />
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
                  <input type="date" value={extractedData.dueDate || ''} on:input={(e) => setExtractedDate('dueDate', e.currentTarget.value)} readonly={!matchingComplete} class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 {!matchingComplete ? 'bg-gray-100 cursor-not-allowed' : !extractedData.dueDate ? 'bg-orange-200 border-orange-400' : dueDateDefaulted ? 'bg-blue-50 border-blue-300' : ''}" />
                  {#if dueDateDefaulted}
                    <p class="text-[10px] text-blue-600 mt-0.5" title="Set in Settings › Bill Sync › Default due date">Default terms: {describeDueTerms(billSettings.directBillDueTerms)}</p>
                  {/if}
                </div>
              </div>

              {#if billSettings.multiCurrencyEnabled}
                <div>
                  <label class="block text-xs font-medium text-gray-700 mb-1">
                    Currency
                    <span class="font-normal text-gray-400">(detected from the bill — change if wrong)</span>
                  </label>
                  <select bind:value={extractedData.currency} disabled={!matchingComplete} class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 {!matchingComplete ? 'bg-gray-100 cursor-not-allowed' : ''}">
                    {#each currencyOptions as cur}
                      <option value={cur}>{cur}</option>
                    {/each}
                    {#if extractedData.currency && !currencyOptions.includes(extractedData.currency)}
                      <option value={extractedData.currency}>{extractedData.currency}</option>
                    {/if}
                  </select>
                  <p class="text-[11px] text-gray-500 mt-1">Syncs to Xero in this currency; converted to your default currency for JobTread.</p>
                </div>
              {/if}

              <!-- Line Items with Assignment -->
              <div>
                <div class="flex items-center justify-between mb-1">
                  <label class="block text-xs font-medium text-gray-700">Line Items *</label>
                  {#if activeCostGroups.length > 0}
                    <div class="inline-flex items-center rounded-md border border-gray-300 overflow-hidden text-xs">
                      <span class="px-1.5 py-0.5 text-[10px] text-gray-500 bg-gray-50 border-r border-gray-300">Search by</span>
                      <button
                        type="button"
                        on:click={() => activeLens = 'costGroup'}
                        class="px-2 py-0.5 transition-colors {activeLens === 'costGroup' ? 'bg-blue-500 text-white font-semibold' : 'bg-white text-gray-700 hover:bg-gray-50'}"
                      >
                        Cost Group
                      </button>
                      <button
                        type="button"
                        on:click={() => activeLens = 'costCode'}
                        class="px-2 py-0.5 border-l border-gray-300 transition-colors {activeLens === 'costCode' ? 'bg-blue-500 text-white font-semibold' : 'bg-white text-gray-700 hover:bg-gray-50'}"
                      >
                        Cost Code
                      </button>
                    </div>
                  {/if}
                </div>

                {#if activeCostGroups.length > 0}
                  <!-- Assign per line, or split the whole bill across a group/code -->
                  <div class="flex items-center gap-2 mb-2 text-xs">
                    <span class="text-[10px] text-gray-500">Assign</span>
                    <div class="inline-flex items-center rounded-md border border-gray-300 overflow-hidden">
                      <button
                        type="button"
                        on:click={() => setSplitMode(false)}
                        class="px-2 py-0.5 transition-colors {!splitMode ? 'bg-blue-500 text-white font-semibold' : 'bg-white text-gray-700 hover:bg-gray-50'}"
                      >
                        Per line
                      </button>
                      <button
                        type="button"
                        on:click={() => setSplitMode(true)}
                        class="px-2 py-0.5 border-l border-gray-300 transition-colors {splitMode ? 'bg-blue-500 text-white font-semibold' : 'bg-white text-gray-700 hover:bg-gray-50'}"
                        title="Spread the whole bill across every budget item in a cost group or cost code, in proportion to each item's budget"
                      >
                        Whole bill (pro-rata)
                      </button>
                    </div>
                  </div>

                  <!-- Cost type filter -->
                  {#if showCostTypeFilter}
                    <div class="flex flex-wrap items-center gap-1 mb-2 text-[10px]">
                      <span class="text-gray-500 mr-0.5">Show types</span>
                      {#each availableCostTypes as type}
                        <button
                          type="button"
                          on:click={() => toggleCostType(type)}
                          class="px-1.5 py-0.5 rounded-full border transition-colors {effectiveCostTypes.includes(type) ? 'bg-blue-500 border-blue-500 text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}"
                        >
                          {type}
                        </button>
                      {/each}
                      {#if effectiveCostTypes.length > 0}
                        <button type="button" on:click={clearCostTypeFilter} class="text-blue-600 hover:underline ml-1">All</button>
                      {/if}
                    </div>
                  {/if}

                  {#if splitMode}
                    <!-- Whole-bill split target + preview -->
                    <div class="mb-2 p-2 border border-blue-200 bg-blue-50/50 rounded text-xs space-y-2">
                      <div class="relative">
                        {#if splitTarget}
                          <div class="flex items-center gap-1">
                            <div
                              class="flex-1 px-1.5 py-1 border-2 rounded font-medium border-green-400 bg-green-50 text-green-800 cursor-pointer truncate"
                              role="button"
                              tabindex="0"
                              on:click={() => { splitTargetDropdownOpen = true; }}
                              on:keydown={(e) => { if (e.key === 'Enter') splitTargetDropdownOpen = true; }}
                            >
                              {splitTarget.kind === 'costCode' ? 'Cost code' : 'Cost group'}: {splitTarget.label}
                            </div>
                            <button class="text-red-400 hover:text-red-600 px-1" on:click={() => { splitTarget = null; }} title="Clear">×</button>
                          </div>
                        {:else}
                          <input
                            type="text"
                            placeholder=" Search for a cost group or cost code to split across "
                            class="w-full px-1.5 py-1 border-2 rounded font-medium border-orange-400 bg-orange-200 text-orange-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            bind:value={splitTargetQuery}
                            on:focus={() => { splitTargetDropdownOpen = true; }}
                            on:blur={() => setTimeout(() => { splitTargetDropdownOpen = false; }, 200)}
                          />
                        {/if}
                        {#if splitTargetDropdownOpen}
                          <div
                            class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-56 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto"
                            on:wheel|stopPropagation
                            on:mousedown|preventDefault
                          >
                            {#if splitTargetList.length === 0}
                              <div class="text-gray-500 px-3 py-2">No cost groups or codes found</div>
                            {:else}
                              {#each splitTargetList as section}
                                <div class="px-2 py-1 text-[10px] font-semibold text-gray-500 bg-gray-50">{section.heading}</div>
                                {#each section.targets as target}
                                  <div
                                    role="button"
                                    tabindex="0"
                                    class="group cursor-pointer select-none py-1.5 pl-4 pr-2 hover:bg-blue-500 hover:text-white"
                                    on:mousedown|preventDefault={() => selectSplitTarget(target)}
                                    on:keydown={(e) => e.key === 'Enter' && selectSplitTarget(target)}
                                  >
                                    <div class="font-medium">{target.label}</div>
                                    <div class="text-[10px] text-gray-500 group-hover:text-white">{target.itemCount} item{target.itemCount === 1 ? '' : 's'} · budget {formatCurrency(target.budget)}</div>
                                  </div>
                                {/each}
                              {/each}
                            {/if}
                          </div>
                        {/if}
                      </div>

                      {#if splitTarget}
                        {#if splitResult.lines.length === 0}
                          <p class="text-amber-700">Nothing to split across — no budget items{effectiveCostTypes.length ? ` of type ${effectiveCostTypes.join(', ')}` : ''} in {splitTarget.label}, or the bill has no amount.</p>
                        {:else}
                          <table class="w-full text-[11px]">
                            <thead>
                              <tr class="text-[10px] text-gray-500 text-left">
                                <th class="font-medium py-0.5">Budget item</th>
                                <th class="font-medium py-0.5 text-right">Budget</th>
                                <th class="font-medium py-0.5 text-right">Share</th>
                                <th class="font-medium py-0.5 text-right">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {#each splitResult.lines as line}
                                <tr class="border-t border-blue-100">
                                  <td class="py-0.5 pr-1">
                                    {line.item.name}
                                    {#if showCostTypeFilter}<span class="ml-1 text-[9px] px-1 rounded bg-gray-100 text-gray-500">{costTypeLabel(line.item)}</span>{/if}
                                  </td>
                                  <td class="py-0.5 text-right text-gray-500">{formatCurrency(line.item.cost || 0)}</td>
                                  <td class="py-0.5 text-right text-gray-500">{(line.ratio * 100).toFixed(1)}%</td>
                                  <td class="py-0.5 text-right font-medium">{formatCurrency(line.amount)}</td>
                                </tr>
                              {/each}
                              <tr class="border-t border-blue-200 font-semibold">
                                <td class="py-0.5" colspan="3">Total (ex tax)</td>
                                <td class="py-0.5 text-right">{formatCurrency(splitBase)}</td>
                              </tr>
                            </tbody>
                          </table>
                          <p class="text-[10px] text-gray-500">
                            JobTread gets one line per budget item above.
                            {#if !isQuickBooks}Xero keeps the supplier's lines as they are.{/if}
                            {#if effectiveCostTypes.length}Only {effectiveCostTypes.join(', ')} items are included (type filter).{/if}
                            {#if splitResult.evenSplit}<span class="text-amber-700">These items have no budget, so the bill is split evenly.</span>{/if}
                          </p>
                        {/if}
                      {/if}
                    </div>
                  {:else if matchingComplete && (extractedData.lineItems || []).length > 1}
                    <!-- Bulk assign -->
                    <div class="flex items-center gap-2 mb-2 text-xs">
                      <label class="inline-flex items-center gap-1 text-gray-600 cursor-pointer flex-shrink-0">
                        <input type="checkbox" class="h-3.5 w-3.5" checked={allLinesBulkSelected} on:change={toggleAllBulkLines} />
                        Select all lines
                      </label>
                      {#if bulkSelectedIndexes.length > 0}
                        <div class="relative flex-1 min-w-0">
                          <input
                            type="text"
                            placeholder=" Assign {bulkSelectedIndexes.length} selected to… "
                            class="w-full px-1.5 py-1 text-xs border-2 rounded font-medium border-blue-400 bg-blue-50 text-blue-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            value={assignmentSearchQueries.bulk || ''}
                            on:input={(e) => { assignmentSearchQueries = { ...assignmentSearchQueries, bulk: e.currentTarget.value }; }}
                            on:focus={() => { bulkDropdownOpen = true; }}
                            on:blur={() => setTimeout(() => { bulkDropdownOpen = false; }, 200)}
                          />
                          {#if bulkDropdownOpen}
                            <CostItemPickerList
                              buckets={getAssignmentBucketsByActiveLens('bulk', assignmentSearchQueries.bulk, activeLens, effectiveCostTypes)}
                              {activeLens}
                              {formatCurrency}
                              cleanName={cleanCostGroupName}
                              showCostTypes={showCostTypeFilter}
                              on:select={(e) => bulkAssign(e.detail)}
                            />
                          {/if}
                        </div>
                      {/if}
                    </div>
                  {/if}
                {/if}

                <div class="space-y-2">
                  {#each (extractedData.lineItems || []) as item, index}
                    <div class="border rounded p-2 text-xs {bulkSelected[index] && !splitMode ? 'border-blue-400 bg-blue-50/40' : 'border-gray-200'}">
                      <div class="flex gap-1 items-start mb-1">
                        {#if activeCostGroups.length > 0 && matchingComplete && !splitMode && (extractedData.lineItems || []).length > 1}
                          <input type="checkbox" class="h-3.5 w-3.5 mt-1.5 flex-shrink-0" checked={!!bulkSelected[index]} on:change={() => toggleBulkLine(index)} title="Select for bulk assign" />
                        {/if}
                        <input type="text" bind:value={item.description} placeholder="Description" readonly={!matchingComplete} class="flex-1 min-w-0 px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 {!matchingComplete ? 'bg-gray-100' : ''}" />
                        <input type="number" bind:value={item.quantity} on:input={() => {updateLineItemAmount(index); validateInvoice();}} placeholder="Qty" min="0" step="0.01" readonly={!matchingComplete} class="w-14 px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 {!matchingComplete ? 'bg-gray-100' : ''}" />
                        <input type="number" bind:value={item.unitPrice} on:input={() => {updateLineItemAmount(index); validateInvoice();}} placeholder="Rate" min="0" step="0.01" readonly={!matchingComplete} class="w-16 px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 {!matchingComplete ? 'bg-gray-100' : ''}" />
                        <input type="number" bind:value={item.amount} placeholder="Amt" class="w-16 px-1 py-1 text-xs border border-gray-300 rounded bg-gray-50" readonly />
                        <div class="relative flex-shrink-0" title="Tax rate for this line (%). Leave blank to use the bill's rate ({defaultLineTaxRate}%).">
                          <input
                            type="number"
                            value={item.taxRateOverride ?? ''}
                            on:change={(e) => setLineTaxRate(index, e.currentTarget.value)}
                            placeholder={String(defaultLineTaxRate)}
                            aria-label="Tax rate % for this line"
                            min="0"
                            step="0.001"
                            readonly={!matchingComplete}
                            class="w-14 pl-1 pr-3.5 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 {item.taxRateOverride != null ? 'border-blue-400 bg-blue-50 text-blue-800 font-medium' : 'border-gray-300'} {!matchingComplete ? 'bg-gray-100' : ''}"
                          />
                          <span class="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">%</span>
                        </div>
                        {#if matchingComplete}
                          <button on:click={() => removeLineItem(index)} class="text-red-500 hover:text-red-700 font-bold px-1">×</button>
                        {/if}
                      </div>
                      {#if lineTaxPlan && lineTaxPlan[index]}
                        <div class="mb-1 text-[10px] {lineTaxPlan[index].overridden ? 'text-blue-700' : 'text-gray-500'}">
                          Tax {lineTaxPlan[index].ratePct}%{lineTaxPlan[index].overridden ? '' : ' (bill rate)'}: {formatCurrency(lineTaxPlan[index].tax)}
                          {#if lineTaxPlan[index].nonRecoverable}<span class="text-gray-400">(GST {formatCurrency(lineTaxPlan[index].recoverable)} + PST {formatCurrency(lineTaxPlan[index].nonRecoverable)})</span>{/if}
                        </div>
                      {/if}
                      <!-- Searchable Assignment dropdown (per-line mode only) -->
                      {#if activeCostGroups.length > 0 && !splitMode}
                        <div class="relative">
                          {#if lineItemAssignments[index]}
                            {@const assignedItem = getAllCostItems().find(ci => ci.id === lineItemAssignments[index])}
                            <div class="flex items-center gap-1">
                              <div
                                class="flex-1 px-1.5 py-1 text-xs border-2 rounded font-medium border-green-400 bg-green-50 text-green-800 cursor-pointer truncate"
                                role="button"
                                tabindex="0"
                                on:click={() => { assignmentDropdownOpen[index] = true; assignmentDropdownOpen = {...assignmentDropdownOpen}; }}
                                on:keydown={(e) => { if (e.key === 'Enter') { assignmentDropdownOpen[index] = true; assignmentDropdownOpen = {...assignmentDropdownOpen}; } }}
                              >
                                {assignedItem?.name || 'Assigned'} ({formatCurrency(assignedItem?.cost)})
                              </div>
                              <button
                                class="text-red-400 hover:text-red-600 text-xs px-1"
                                on:click={() => assignLineItem(index, null)}
                                title="Clear assignment"
                              >×</button>
                            </div>
                          {:else}
                            <input
                              type="text"
                              placeholder=" Search and Assign to JobTread cost item "
                              class="w-full px-1.5 py-1 text-xs border-2 rounded font-medium border-orange-400 bg-orange-200 text-orange-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              value={assignmentSearchQueries[index] || ''}
                              on:input={(e) => { assignmentSearchQueries[index] = e.target.value; assignmentSearchQueries = {...assignmentSearchQueries}; }}
                              on:focus={() => { assignmentDropdownOpen[index] = true; assignmentDropdownOpen = {...assignmentDropdownOpen}; }}
                              on:blur={() => setTimeout(() => { assignmentDropdownOpen[index] = false; assignmentDropdownOpen = {...assignmentDropdownOpen}; }, 200)}
                            />
                          {/if}
                          {#if assignmentDropdownOpen[index]}
                            <CostItemPickerList
                              buckets={getAssignmentBucketsByActiveLens(index, assignmentSearchQueries[index], activeLens, effectiveCostTypes)}
                              {activeLens}
                              {formatCurrency}
                              cleanName={cleanCostGroupName}
                              showCostTypes={showCostTypeFilter}
                              on:select={(e) => selectAssignmentItem(index, e.detail)}
                            />
                          {/if}
                        </div>
                      {/if}
                    </div>
                  {/each}
                  {#if matchingComplete}
                    <button
                      type="button"
                      on:click={addLineItem}
                      class="w-full px-2 py-1.5 text-xs text-blue-600 border border-dashed border-blue-300 rounded hover:bg-blue-50"
                    >
                      + Add line item
                    </button>
                  {/if}
                </div>
              </div>

              <!-- Totals -->
              <div class="border-t border-gray-200 pt-3 space-y-1.5">
                <div class="flex justify-between text-xs items-center">
                  <span class="text-gray-700">Subtotal:</span>
                  <input type="number" bind:value={extractedData.subtotal} on:input={recalculateTotal} min="0" step="0.01" readonly={!matchingComplete} class="w-24 px-1.5 py-1 text-xs text-right border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 {!matchingComplete ? 'bg-gray-100' : !isLineItemsValid ? 'bg-orange-200' : ''}" />
                </div>
                {#if lineTaxMode}
                  <div class="flex justify-between text-xs items-center">
                    <span class="text-gray-700">Tax <span class="text-[10px] text-gray-500">(worked out per line)</span>:</span>
                    <input type="number" value={extractedData.taxAmount} readonly class="w-24 px-1.5 py-1 text-xs text-right border border-gray-300 rounded bg-gray-50" />
                  </div>
                  {#if lineTaxPst}
                    <div class="flex justify-between text-[10px] text-gray-500">
                      <span>incl. PST (non-recoverable)</span>
                      <span>{formatCurrency(lineTaxPst)}</span>
                    </div>
                  {/if}
                  <div class="flex justify-between text-[10px] text-gray-500 gap-2">
                    <span>Lines without their own rate: {defaultLineTaxRate}%{extractedData.lineTaxBaseline ? ` · tax before line rates ${formatCurrency(extractedData.lineTaxBaseline.taxAmount)}` : ''}</span>
                    {#if matchingComplete}
                      <button type="button" on:click={resetLineTaxRates} class="text-blue-600 hover:underline flex-shrink-0">Reset line tax rates</button>
                    {/if}
                  </div>
                {:else if caTaxActive}
                  {#each extractedData.taxBreakdown as row, i}
                    <div class="flex justify-between text-xs items-center">
                      <span class="text-gray-700">
                        {row.code} {row.rate}%:
                        {#if row.code === 'PST'}<span class="text-[10px] text-gray-400" title="Posted with GST at the combined rate. Xero adds it to the expense when the rate's PST component is Non-recoverable; JobTread adds it to the job cost and shows it as PST on the bill, with GST as the tax.">(non-recoverable)</span>{/if}
                      </span>
                      <input type="number" value={row.amount} on:input={(e) => setBreakdownAmount(i, e.currentTarget.value)} min="0" step="0.01" readonly={!matchingComplete} class="w-24 px-1.5 py-1 text-xs text-right border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 {!matchingComplete ? 'bg-gray-100' : ''}" />
                    </div>
                  {/each}
                  <div class="flex justify-between text-[10px] text-gray-500">
                    <span>{PROVINCES[caProvince]?.name}: each tax on the pre-tax amount</span>
                    {#if matchingComplete && !extractedData.taxBreakdownAuto}
                      <button type="button" on:click={recalculateProvinceTax} class="text-blue-600 hover:underline">Use {caProvince} rates</button>
                    {/if}
                  </div>
                  {#if caTaxMismatch}
                    <div class="p-1.5 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800">
                      The bill shows {formatCurrency(extractedData.billTaxAmount)} tax; {extractedData.taxBreakdownAuto ? `${caProvince} rates give` : 'these rows add to'} {formatCurrency(extractedData.taxAmount)}.
                      {#if matchingComplete}
                        <button type="button" on:click={useBillTax} class="ml-1 text-blue-600 hover:underline">Use the bill's tax</button>
                      {/if}
                    </div>
                  {/if}
                {:else}
                  <div class="flex justify-between text-xs items-center">
                    <span class="text-gray-700">Tax:</span>
                    <input type="number" bind:value={extractedData.taxAmount} on:input={recalculateTotal} min="0" step="0.01" readonly={!matchingComplete} class="w-24 px-1.5 py-1 text-xs text-right border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 {!matchingComplete ? 'bg-gray-100' : ''}" />
                  </div>
                {/if}
                <div class="flex justify-between text-sm font-semibold pt-1.5 border-t border-gray-200 items-center">
                  <span>Total:</span>
                  <input
                    type="number"
                    bind:value={extractedData.total}
                    on:input={onTotalInput}
                    step="0.01"
                    readonly={!matchingComplete}
                    class="w-24 px-1.5 py-1 text-xs text-right border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-semibold {!matchingComplete ? 'bg-gray-100' : !isTotalValid ? 'bg-orange-200 border-orange-400' : totalOverridden ? 'bg-yellow-50 border-yellow-400' : ''}"
                  />
                </div>
                {#if totalOverridden && matchingComplete}
                  <div class="flex justify-end">
                    <button
                      type="button"
                      on:click={clearTotalOverride}
                      class="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      Recalculate from lines
                    </button>
                  </div>
                {/if}
                {#if !isLineItemsValid || !isTotalValid || totalModified}
                  <div class="p-1.5 bg-orange-50 border border-orange-300 rounded text-xs text-orange-800 space-y-0.5">
                    {#if !isLineItemsValid}
                      <div>Line items don't add up to subtotal</div>
                    {/if}
                    {#if !isTotalValid}
                      <div>Subtotal + tax doesn't match total</div>
                    {/if}
                    {#if totalModified}
                      <div>Total differs from PDF ({formatCurrency(originalTotal)})</div>
                    {/if}
                  </div>
                {/if}
              </div>
            </div>

            <!-- Sync / Submit action — placed below the assignment section so the user completes job + line-item assignments before syncing -->
            <div class="lg:hidden {currentBillSynced ? 'opacity-50 pointer-events-none' : ''}">
            <!-- Sync / Submit for Approval Button -->
            <div class="border-t border-gray-200 pt-3 mt-3">
              <!-- Overhead routing: send to accounting only, exclude from JobTread.
                   Credit notes are allowed through for QuickBooks orgs, where they
                   post as a VendorCredit; the Xero path still excludes them. -->
              {#if !readOnly && (!extractedData.isCreditNote || isQuickBooks)}
                <label class="flex items-center gap-2 mb-2 text-xs text-gray-700 cursor-pointer">
                  <input type="checkbox" bind:checked={accountingOnly} on:change={onAccountingOnlyChange} class="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span>Send overhead to {overheadDestShort} only</span>
                </label>
                {#if accountingOnly && isQuickBooks}
                  <div class="mb-3">
                    <label class="block text-xs font-medium text-gray-700 mb-1">Overhead expense account (QuickBooks)</label>
                    <select
                      bind:value={overheadAccountCode}
                      class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Use the default overhead account{qboDefaultAccountName ? ` (${qboDefaultAccountName})` : ''}</option>
                      {#each qboExpenseAccounts as acct}
                        <option value={acct.id}>{acct.name}</option>
                      {/each}
                    </select>
                    {#if !qboExpenseAccountsLoaded}
                      <p class="text-[11px] text-gray-400 mt-0.5">Loading expense accounts…</p>
                    {:else if !qboConnected}
                      <p class="text-[11px] text-amber-600 mt-0.5">QuickBooks isn't connected — connect it in <a href="/settings/quickbooks" class="underline">Settings &rsaquo; QuickBooks</a>.</p>
                    {:else if qboExpenseAccounts.length === 0}
                      <p class="text-[11px] text-amber-600 mt-0.5">No QuickBooks expense accounts found.</p>
                    {:else if !qboDefaultAccountId && !overheadAccountCode}
                      <p class="text-[11px] text-amber-600 mt-0.5">No default overhead account set — pick one here or set a default in Settings.</p>
                    {/if}
                  </div>
                  {#if markAsPaid && !extractedData.isCreditNote}
                    <div class="mb-3">
                      <label class="block text-xs font-medium text-gray-700 mb-1">Paid from (QuickBooks)</label>
                      <select
                        bind:value={qboPaymentAccountId}
                        class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">Use the default{qboDefaultPaymentAccountName ? ` (${qboDefaultPaymentAccountName})` : ''}</option>
                        {#each qboPaymentAccounts as acct}
                          <option value={acct.id}>{acct.name} ({acct.accountType})</option>
                        {/each}
                      </select>
                      {#if qboPaidNeedsAccount}
                        <p class="text-[11px] text-amber-600 mt-0.5">
                          Pick the account this was paid from — or set a default in
                          <a href="/settings/quickbooks" class="underline">Settings &rsaquo; QuickBooks</a>.
                          Otherwise it would post to Accounts Payable as an unpaid bill.
                        </p>
                      {:else}
                        <p class="text-[11px] text-gray-500 mt-0.5">Posts as a QuickBooks Expense, not a bill in Accounts Payable.</p>
                      {/if}
                    </div>
                  {/if}
                {:else if accountingOnly}
                  <div class="mb-3">
                    <label class="block text-xs font-medium text-gray-700 mb-1">Overhead expense account (Xero)</label>
                    <select
                      bind:value={overheadAccountCode}
                      class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Use my default expense code{userExpenseCode ? ` (${userExpenseCode})` : ''}</option>
                      {#each xeroExpenseAccounts as acct}
                        <option value={acct.code}>{acct.code} — {acct.name}</option>
                      {/each}
                    </select>
                    {#if !xeroExpenseAccountsLoaded}
                      <p class="text-[11px] text-gray-400 mt-0.5">Loading expense accounts…</p>
                    {:else if xeroExpenseAccounts.length === 0}
                      <p class="text-[11px] text-amber-600 mt-0.5">No Xero expense accounts found — the default expense code will be used.</p>
                    {/if}
                  </div>
                {/if}
              {/if}
              {#if !readOnly && !extractedData.isCreditNote}
                <label class="flex items-center gap-2 mb-3 text-xs text-gray-700 cursor-pointer">
                  <input type="checkbox" bind:checked={markAsPaid} class="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span>{qboOverheadMode ? 'Already paid (post as Expense)' : 'Mark as Paid'}</span>
                </label>
              {/if}
              {#if currentBill?.possibleDuplicates?.length && !readOnly}
                <div class="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <p class="font-semibold">Possible duplicate</p>
                  {#each currentBill.possibleDuplicates as d}
                    <p>{d.reason === 'same-file' ? 'Same file as' : 'Same supplier and invoice number as'} {d.fileName || d.uploadId} ({d.status}{d.uploadedAt ? ', uploaded ' + String(d.uploadedAt).slice(0, 10) : ''})</p>
                  {/each}
                  <p class="mt-1">Check it isn't the same bill before syncing.</p>
                </div>
              {/if}
              {#if readOnly}
                <div class="w-full px-4 py-2.5 text-sm font-medium rounded-md bg-gray-100 text-gray-500 text-center">
                  View Only — Already Synced
                </div>
                <p class="mt-2 text-xs text-gray-600">
                  Voiding this bill in Xero or deleting it in JobTread does not reset Buildzilla.
                </p>
                <button
                  on:click={() => clearSyncedStatus()}
                  disabled={clearingSynced}
                  class="mt-2 w-full px-4 py-2 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {clearingSynced ? 'Clearing…' : 'Clear synced status to push again'}
                </button>
              {:else if showSubmitForApproval}
                <button
                  on:click={submitBillForApproval}
                  disabled={!readyToSync}
                  class="w-full px-4 py-2.5 text-sm font-medium rounded-md transition-colors
                    {readyToSync ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}"
                >
                  {#if submittingForApproval}
                    <span class="flex items-center justify-center">
                      <svg class="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  {:else}
                    Submit for Approval
                  {/if}
                </button>
              {:else}
                <button
                  on:click={syncBoth}
                  disabled={!readyToSync}
                  class="w-full px-4 py-2.5 text-sm font-medium rounded-md transition-colors
                    {readyToSync ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}"
                >
                  {#if syncing}
                    <span class="flex items-center justify-center">
                      <svg class="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {syncProgress.step}
                    </span>
                  {:else if currentBillSynced}
                    Already synced
                  {:else}
                    {accountingOnly ? (qboOverheadMode ? `Send ${qboDocLabel} to QB` : `Send overhead to ${overheadDestShort}`) : isQuickBooks ? 'Sync to JobTread' : 'Sync to Xero + JobTread'}
                  {/if}
                </button>
              {/if}
              {#if !readyToSync && !syncing && !submittingForApproval && !syncLocked}
                <div class="mt-2 text-xs text-gray-500 space-y-0.5">
                  {#if !vendorReady}<p>- Select or match a vendor</p>{/if}
                  {#if qboPaidNeedsAccount}<p>- Choose which account this was paid from</p>{/if}
                  {#if !accountingOnly && !matchedJob && !matchedPO}<p>- Select a job or PO</p>{/if}
                  {#if !accountingOnly && !assignmentsComplete}<p>- {splitMode ? 'Pick a cost group or cost code to split the bill across' : 'Assign all line items to cost items'}</p>{/if}
                  {#if !hasDates}<p>- Issue Date and Due Date are required</p>{/if}
                </div>
              {/if}
              {#if clearedSyncedNote && !readOnly}
                <p class="mt-2 text-xs text-green-700">Synced status cleared. Review the bill and click Sync to push it again.</p>
              {/if}
            </div>
            </div>
              <!-- Sync results render directly under the sync button, in the same
                   column, so the user sees the outcome + links and can still move
                   to the next selected bill via the nav at the top. -->
              {#if syncResults.length > 0}
                <div class="lg:hidden">
                  <SyncResults
                    {syncResults}
                    {selectedBills}
                    {overheadDest}
                    {userExpenseCode}
                    {syncing}
                    {accountingOnly}
                    showBack={false}
                    onRetry={retryXeroWithDefaultCode}
                    onLinkExisting={linkToExistingXeroBill}
                    onCreateAnyway={() => syncBoth({ allowDuplicate: true })}
                    onClearSynced={clearSyncedStatus}
                    {clearingSynced}
                    onBack={goBack}
                  />
                </div>
              {/if}
          </div>

          <!-- Matching Panel (3/12 = 25%) — first on mobile -->
          <div bind:this={matchingColEl} class="order-1 lg:order-none lg:col-span-3 p-4 lg:overflow-y-auto lg:min-h-0">
            <div class="{currentBillSynced ? 'opacity-50 pointer-events-none' : ''}">
            <h2 class="text-sm font-semibold mb-3">Match to JobTread</h2>

            {#if accountingOnly}
              <div class="mb-3 p-3 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800">
                <p class="font-medium">Overhead — {overheadDest} only</p>
                <p class="text-amber-700 mt-0.5">This bill will be sent to {overheadDest} and <strong>not</strong> to JobTread. Job matching is not needed.</p>
              </div>
            {:else}
            <!-- Mode Toggle -->
            <div class="flex items-center justify-center mb-3 p-2 bg-gray-50 rounded border text-xs">
              <span class="font-medium text-gray-700 mr-2">Mode:</span>
              <span class="font-medium {!isJobMode ? 'text-blue-600' : 'text-gray-400'}">PO</span>
              <label class="relative inline-flex items-center cursor-pointer mx-2">
                <input type="checkbox" bind:checked={isJobMode} on:change={() => {
                  markMatchTouched();
                  fetch('/api/users/bill-settings', {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ billmodedefaultjob: isJobMode })
                  }).then(() => console.log('Bill mode default saved:', isJobMode ? 'Job' : 'PO'))
                    .catch(err => console.error('Failed to save bill mode default:', err));
                  // Re-run selectCustomer so matchedJob/matchedPO, cost groups,
                  // and vendor matching update for the new mode.
                  if (selectedCustomer) selectCustomer(selectedCustomer);
                }} class="sr-only peer" />
                <div class="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
              <span class="font-medium {isJobMode ? 'text-blue-600' : 'text-gray-400'}">Job</span>
            </div>

            <!-- Job/Customer Search -->
            <div class="mb-3">
              <label class="block text-xs font-medium mb-1 {!selectedCustomer ? 'text-orange-700 font-semibold' : 'text-gray-700'}">{!selectedCustomer ? 'Select a Job' : isJobMode ? 'Job' : 'Customer'}</label>
              <div class="relative">
                <div class="relative">
                <input
                  type="text"
                  placeholder="{isJobMode ? 'Search for a job...' : 'Search for a customer...'}"
                  class="w-full px-2 py-1.5 text-sm {!selectedCustomer ? 'border-2 border-orange-500 bg-orange-50' : 'border border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 {selectedCustomer ? 'pr-7' : ''}"
                  autocomplete="off"
                  bind:value={customerSearchQuery}
                  bind:this={customerInputElement}
                  on:input={handleCustomerSearch}
                  on:focus={() => { showCustomerDropdown = true; filterCustomers(); }}
                  on:blur={() => handleInputFocusOut('customer')}
                />
                {#if selectedCustomer}
                  <button
                    type="button"
                    title="Clear job selection"
                    aria-label="Clear job selection"
                    class="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
                    on:mousedown|preventDefault={clearCustomerSelection}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                  </button>
                {/if}
                </div>
                {#if showCustomerDropdown}
                  <div
                    class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-48 rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 overflow-auto"
                    on:wheel|stopPropagation
                    on:mousedown|preventDefault
                  >
                    {#if filteredCustomers.length === 0}
                      <div class="text-gray-500 px-3 py-2 text-xs">No results</div>
                    {:else}
                      {#each filteredCustomers as customer}
                        <div role="button" tabindex="0" class="cursor-pointer py-1.5 px-3 text-xs hover:bg-blue-500 hover:text-white {customer.id === selectedCustomer ? 'bg-blue-100' : ''}" on:mousedown|preventDefault={() => selectCustomer(customer.id)} on:keydown={(e) => e.key === 'Enter' && selectCustomer(customer.id)}>
                          {customer.name}
                        </div>
                      {/each}
                    {/if}
                  </div>
                {/if}
              </div>
            </div>

            <!-- PO Search (PO mode only) -->
            {#if !isJobMode}
              <div class="mb-3">
                <label class="block text-xs font-medium text-gray-700 mb-1">Vendor Order</label>
                <div class="relative">
                  {#if isLoadingVendorOrders}
                    <div class="border border-gray-300 rounded-md p-2 flex items-center justify-center text-xs">
                      <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                      Loading...
                    </div>
                  {:else}
                    <div class="relative">
                    <input
                      type="text"
                      placeholder="Search vendor orders..."
                      class="w-full px-2 py-1.5 text-sm {selectedCustomer && !selectedVendorOrder ? 'border-2 border-orange-400' : 'border border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 {selectedVendorOrder ? 'pr-7' : ''}"
                      autocomplete="off"
                      bind:value={vendorOrderSearchQuery}
                      bind:this={vendorOrderInputElement}
                      on:input={handleVendorOrderSearch}
                      on:focus={() => { showVendorOrderDropdown = true; filteredVendorOrders = vendorOrders; }}
                      on:blur={() => handleInputFocusOut('vendorOrder')}
                    />
                    {#if selectedVendorOrder}
                      <button
                        type="button"
                        title="Clear vendor order selection"
                        aria-label="Clear vendor order selection"
                        class="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
                        on:mousedown|preventDefault={clearVendorOrderSelection}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                        </svg>
                      </button>
                    {/if}
                    </div>
                    {#if showVendorOrderDropdown}
                      <div
                        class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-48 rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 overflow-auto"
                        on:wheel|stopPropagation
                        on:mousedown|preventDefault
                      >
                        {#if filteredVendorOrders.length === 0}
                          <div class="text-gray-500 px-3 py-2 text-xs">No vendor orders</div>
                        {:else}
                          {#each filteredVendorOrders as order}
                            <div role="button" tabindex="0" class="cursor-pointer py-1.5 px-3 text-xs hover:bg-blue-500 hover:text-white" on:mousedown|preventDefault={() => selectVendorOrder(order.id)} on:keydown={(e) => e.key === 'Enter' && selectVendorOrder(order.id)}>
                              <span class="font-medium">PO #{order.number}</span>
                              {#if order.subject}<span class="text-gray-500 ml-1">{order.subject.substring(0, 30)}</span>{/if}
                            </div>
                          {/each}
                        {/if}
                      </div>
                    {/if}
                  {/if}
                </div>
              </div>
            {/if}

            <!-- Matched Status -->
            {#if matchedJob}
              <div class="mb-3 p-2 bg-green-50 border border-green-200 rounded text-xs">
                <span class="text-green-700 font-medium">Job: {matchedJob.jobName}</span>
              </div>
            {/if}
            {#if matchedPO}
              <div class="mb-3 p-2 bg-green-50 border border-green-200 rounded text-xs">
                <span class="text-green-700 font-medium">PO #{matchedPO.vendorOrderNumber} - {matchedPO.jobName}</span>
              </div>
            {/if}

            <!-- Cost Groups loading / empty budget warning -->
            {#if isLoadingCostGroups}
              <div class="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs flex items-center">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                Loading cost groups...
              </div>
            {:else if activeCostGroups.length > 0}
              <div class="mb-3 p-2 bg-gray-50 border border-gray-200 rounded text-xs flex items-center justify-between">
                <span class="text-gray-600">{activeCostGroups.length} cost groups loaded</span>
                <button type="button" on:click={refreshCostGroups} class="text-blue-600 hover:text-blue-700 text-xs">Refresh</button>
              </div>
            {:else if noBudgetLoaded}
              <div class="mb-3 p-2 bg-amber-50 border border-amber-300 rounded text-xs">
                <div class="flex items-start justify-between gap-2">
                  <div>
                    <p class="font-medium text-amber-800">No budget lines on this {isJobMode ? 'job' : 'PO'}</p>
                    <p class="text-amber-700 mt-0.5">
                      {#if isJobMode}
                        Direct Bill Import needs JobTread budget cost items. This job has none (costs may only exist on POs/bills).
                      {:else}
                        This PO has no line items/cost groups to assign against.
                      {/if}
                    </p>
                  </div>
                  <button type="button" on:click={refreshCostGroups} class="shrink-0 text-amber-800 hover:text-amber-900 underline font-medium">Refresh</button>
                </div>
              </div>
            {/if}
            {/if}

            <!-- Vendor Matching Section -->
            <div class="border-t border-gray-200 pt-3 mt-3">
              <h3 class="text-xs font-semibold mb-2 text-gray-700">Vendor Match{qboOverheadMode ? ' (QuickBooks)' : ''}</h3>

              {#if qboOverheadMode}
                <!-- Overhead goes straight to QuickBooks, so match against QUICKBOOKS
                     vendors. The JobTread vendor list is irrelevant here. -->
                {#if isLoadingQboVendors}
                  <div class="p-2 bg-blue-50 border border-blue-200 rounded text-xs flex items-center">
                    <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                    Searching QuickBooks vendors...
                  </div>
                {:else if !qboConnected && qboExpenseAccountsLoaded}
                  <div class="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                    QuickBooks isn't connected — connect it in
                    <a href="/settings/quickbooks" class="underline">Settings &rsaquo; QuickBooks</a>.
                  </div>
                {:else}
                  {#if selectedQboVendorId}
                    <div class="mb-2 p-2 bg-green-50 border border-green-200 rounded text-xs flex items-center justify-between gap-2">
                      <span class="text-green-700 font-medium truncate">QuickBooks vendor: {qboVendorQuery}</span>
                      <button
                        type="button"
                        title="Clear match"
                        aria-label="Clear QuickBooks vendor match"
                        class="flex-shrink-0 text-green-700 hover:text-red-600 leading-none"
                        on:click={clearQboVendor}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  {/if}
                  <div class="relative mb-2">
                    <input
                      type="text"
                      placeholder="Search QuickBooks vendors..."
                      class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      bind:value={qboVendorQuery}
                      on:input={onQboVendorQueryInput}
                      on:focus={() => (showQboVendorDropdown = true)}
                      on:blur={() => setTimeout(() => (showQboVendorDropdown = false), 150)}
                    />
                    {#if showQboVendorDropdown && filteredQboVendors.length > 0}
                      <div
                        class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-48 rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 overflow-auto"
                        on:wheel|stopPropagation
                        on:mousedown|preventDefault
                      >
                        {#each filteredQboVendors as vendor}
                          <div role="button" tabindex="0" class="cursor-pointer py-1.5 px-3 text-xs hover:bg-blue-500 hover:text-white" on:mousedown|preventDefault={() => selectQboVendor(vendor)} on:keydown={(e) => e.key === 'Enter' && selectQboVendor(vendor)}>
                          {vendor.displayName}
                          </div>
                        {/each}
                      </div>
                    {/if}
                  </div>
                  {#if !selectedQboVendorId}
                    <p class="text-[11px] text-gray-500">
                      No match selected — "{(extractedData.vendorName || '').trim() || 'this supplier'}" will be created as a new vendor in QuickBooks.
                    </p>
                  {/if}
                {/if}
              {:else if isAutoMatchingVendor}
                <div class="p-2 bg-blue-50 border border-blue-200 rounded text-xs flex items-center">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  Matching vendor...
                </div>
              {:else if autoMatchedVendor && selectedVendorId}
                <div class="p-2 bg-green-50 border border-green-200 rounded text-xs flex items-center justify-between gap-2">
                  <span class="text-green-700 font-medium">Matched: {vendorSearchQuery}</span>
                  <button
                    type="button"
                    title="Clear match"
                    aria-label="Clear vendor match"
                    class="flex-shrink-0 text-green-700 hover:text-red-600 leading-none"
                    on:click={clearVendorMatch}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              {:else if pendingVendorCandidates.length > 0}
                <div class="p-2 bg-blue-50 border border-blue-200 rounded text-xs mb-2">
                  <span class="text-blue-700">Close matches for "{pendingVendorSourceName}" — please confirm:</span>
                </div>
                <div class="space-y-1.5 mb-2">
                  {#each pendingVendorCandidates as candidate}
                    <div class="flex items-center justify-between bg-white rounded border border-gray-200 px-2 py-1.5">
                      <span class="text-xs font-medium text-gray-800">{candidate.name}</span>
                      <button
                        class="px-2 py-0.5 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded"
                        on:click={() => confirmVendorCandidate(candidate, pendingVendorSourceName)}
                      >
                        Confirm
                      </button>
                    </div>
                  {/each}
                </div>
                <button
                  class="w-full px-2 py-1 text-xs bg-gray-100 border border-gray-300 rounded hover:bg-gray-200"
                  on:click={rejectVendorCandidates}
                >
                  None of these
                </button>
              {:else if autoMatchFailed}
                <div class="p-2 bg-orange-50 border border-orange-200 rounded text-xs mb-2">
                  <span class="text-orange-700">No auto-match found for "{extractedData.vendorName}"</span>
                </div>
                <div class="relative mb-2">
                  <input
                    type="text"
                    placeholder="Search vendors..."
                    class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    bind:value={vendorSearchQuery}
                    on:input={handleVendorSearch}
                    on:focus={() => { showVendorDropdown = true; filterVendorList(); }}
                    on:blur={() => handleInputFocusOut('vendor')}
                  />
                  {#if showVendorDropdown}
                    <div
                      class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-48 rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 overflow-auto"
                      on:wheel|stopPropagation
                      on:mousedown|preventDefault
                    >
                      {#each filteredVendors as vendor}
                        <div role="button" tabindex="0" class="cursor-pointer py-1.5 px-3 text-xs hover:bg-blue-500 hover:text-white" on:mousedown|preventDefault={() => selectVendor(vendor.id)} on:keydown={(e) => e.key === 'Enter' && selectVendor(vendor.id)}>
                          {vendor.name}
                        </div>
                      {/each}
                    </div>
                  {/if}
                </div>
                <RequiredVendorCustomField
                  fields={requiredVendorFields}
                  bind:values={requiredVendorFieldValues}
                  message={requiredVendorError}
                />
                <button on:click={createNewVendor} disabled={isCreatingVendor || !(extractedData.vendorName || '').trim() || !requiredCustomFieldsFilled(requiredVendorFields, requiredVendorFieldValues)} class="w-full px-2 py-1.5 text-xs bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 disabled:opacity-50">
                  {isCreatingVendor ? 'Creating...' : `Create "${extractedData.vendorName || 'vendor'}" in JobTread`}
                </button>
              {/if}
            </div>



            <!-- Xero contact (Xero orgs): matched, picked, or created on purpose -->
            {#if xeroContactNeeded}
            <div class="border-t border-gray-200 pt-3 mt-3">
              <h3 class="text-xs font-semibold mb-2 text-gray-700">Xero Contact</h3>
              {#if xeroContactLoading}
                <div class="p-2 bg-blue-50 border border-blue-200 rounded text-xs flex items-center">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  Finding the Xero contact...
                </div>
              {:else if xeroContact}
                <div class="p-2 bg-green-50 border border-green-200 rounded text-xs flex items-center justify-between gap-2">
                  <span class="text-green-700 font-medium truncate">
                    Xero contact: {xeroContact.name}
                    <span class="font-normal text-green-600">({xeroContact.how === 'remembered' ? 'matched before' : xeroContact.how === 'chosen' ? 'chosen' : xeroContact.how === 'exact' ? 'exact match' : 'name match'})</span>
                  </span>
                  <button type="button" title="Change contact" aria-label="Change Xero contact" class="flex-shrink-0 text-green-700 hover:text-red-600 leading-none" on:click={clearXeroContact}>
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              {:else if xeroContactCreateNew}
                <div class="p-2 bg-amber-50 border border-amber-200 rounded text-xs flex items-center justify-between gap-2">
                  <span class="text-amber-800">A new Xero contact "{(extractedData.vendorName || '').trim()}" will be created.</span>
                  <button type="button" class="text-amber-800 underline flex-shrink-0" on:click={() => (xeroContactCreateNew = false)}>Undo</button>
                </div>
              {:else}
                {#if xeroContactError}
                  <div class="p-2 mb-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">{xeroContactError}</div>
                {:else}
                  <div class="p-2 mb-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-700">
                    No Xero contact matches "{(extractedData.vendorName || '').trim() || 'this supplier'}". Choose one:
                  </div>
                {/if}
                {#if xeroContactCandidates.length > 0}
                  <div class="space-y-1.5 mb-2">
                    {#each xeroContactCandidates as candidate}
                      <div class="flex items-center justify-between bg-white rounded border border-gray-200 px-2 py-1.5">
                        <span class="text-xs font-medium text-gray-800 truncate">{candidate.name}</span>
                        <button class="px-2 py-0.5 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded flex-shrink-0" on:click={() => chooseXeroContact(candidate)}>Use</button>
                      </div>
                    {/each}
                  </div>
                {/if}
                <div class="relative mb-2">
                  <input
                    type="text"
                    placeholder="Search Xero contacts..."
                    class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    bind:value={xeroContactQuery}
                    on:input={onXeroContactSearch}
                    on:focus={() => (showXeroContactDropdown = true)}
                    on:blur={() => setTimeout(() => (showXeroContactDropdown = false), 150)}
                  />
                  {#if showXeroContactDropdown && xeroContactSearchResults.length > 0}
                    <div class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-48 rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 overflow-auto" on:wheel|stopPropagation on:mousedown|preventDefault>
                      {#each xeroContactSearchResults as c}
                        <div role="button" tabindex="0" class="cursor-pointer py-1.5 px-3 text-xs hover:bg-blue-500 hover:text-white" on:mousedown|preventDefault={() => chooseXeroContact(c)} on:keydown={(e) => e.key === 'Enter' && chooseXeroContact(c)}>{c.name}</div>
                      {/each}
                    </div>
                  {/if}
                </div>
                <button
                  type="button"
                  class="w-full px-2 py-1.5 text-xs bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 disabled:opacity-50"
                  disabled={!(extractedData.vendorName || '').trim()}
                  on:click={() => (xeroContactCreateNew = true)}
                >Create new Xero contact "{(extractedData.vendorName || '').trim() || 'supplier'}"</button>
              {/if}
            </div>
            {/if}

            <!-- Sync / Submit action — desktop keeps it in the right-hand Match column,
                 pinned to the bottom so it stays visible when the column scrolls -->
            <div class="hidden lg:block lg:sticky lg:bottom-0 lg:z-10 bg-white -mx-4 px-4 -mb-4 pb-4">
            <!-- Sync / Submit for Approval Button -->
            <div class="border-t border-gray-200 pt-3 mt-3">
              <!-- Overhead routing: send to accounting only, exclude from JobTread.
                   Credit notes are allowed through for QuickBooks orgs, where they
                   post as a VendorCredit; the Xero path still excludes them. -->
              {#if !readOnly && (!extractedData.isCreditNote || isQuickBooks)}
                <label class="flex items-center gap-2 mb-2 text-xs text-gray-700 cursor-pointer">
                  <input type="checkbox" bind:checked={accountingOnly} on:change={onAccountingOnlyChange} class="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span>Send overhead to {overheadDestShort} only</span>
                </label>
                {#if accountingOnly && isQuickBooks}
                  <div class="mb-3">
                    <label class="block text-xs font-medium text-gray-700 mb-1">Overhead expense account (QuickBooks)</label>
                    <select
                      bind:value={overheadAccountCode}
                      class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Use the default overhead account{qboDefaultAccountName ? ` (${qboDefaultAccountName})` : ''}</option>
                      {#each qboExpenseAccounts as acct}
                        <option value={acct.id}>{acct.name}</option>
                      {/each}
                    </select>
                    {#if !qboExpenseAccountsLoaded}
                      <p class="text-[11px] text-gray-400 mt-0.5">Loading expense accounts…</p>
                    {:else if !qboConnected}
                      <p class="text-[11px] text-amber-600 mt-0.5">QuickBooks isn't connected — connect it in <a href="/settings/quickbooks" class="underline">Settings &rsaquo; QuickBooks</a>.</p>
                    {:else if qboExpenseAccounts.length === 0}
                      <p class="text-[11px] text-amber-600 mt-0.5">No QuickBooks expense accounts found.</p>
                    {:else if !qboDefaultAccountId && !overheadAccountCode}
                      <p class="text-[11px] text-amber-600 mt-0.5">No default overhead account set — pick one here or set a default in Settings.</p>
                    {/if}
                  </div>
                  {#if markAsPaid && !extractedData.isCreditNote}
                    <div class="mb-3">
                      <label class="block text-xs font-medium text-gray-700 mb-1">Paid from (QuickBooks)</label>
                      <select
                        bind:value={qboPaymentAccountId}
                        class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">Use the default{qboDefaultPaymentAccountName ? ` (${qboDefaultPaymentAccountName})` : ''}</option>
                        {#each qboPaymentAccounts as acct}
                          <option value={acct.id}>{acct.name} ({acct.accountType})</option>
                        {/each}
                      </select>
                      {#if qboPaidNeedsAccount}
                        <p class="text-[11px] text-amber-600 mt-0.5">
                          Pick the account this was paid from — or set a default in
                          <a href="/settings/quickbooks" class="underline">Settings &rsaquo; QuickBooks</a>.
                          Otherwise it would post to Accounts Payable as an unpaid bill.
                        </p>
                      {:else}
                        <p class="text-[11px] text-gray-500 mt-0.5">Posts as a QuickBooks Expense, not a bill in Accounts Payable.</p>
                      {/if}
                    </div>
                  {/if}
                {:else if accountingOnly}
                  <div class="mb-3">
                    <label class="block text-xs font-medium text-gray-700 mb-1">Overhead expense account (Xero)</label>
                    <select
                      bind:value={overheadAccountCode}
                      class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Use my default expense code{userExpenseCode ? ` (${userExpenseCode})` : ''}</option>
                      {#each xeroExpenseAccounts as acct}
                        <option value={acct.code}>{acct.code} — {acct.name}</option>
                      {/each}
                    </select>
                    {#if !xeroExpenseAccountsLoaded}
                      <p class="text-[11px] text-gray-400 mt-0.5">Loading expense accounts…</p>
                    {:else if xeroExpenseAccounts.length === 0}
                      <p class="text-[11px] text-amber-600 mt-0.5">No Xero expense accounts found — the default expense code will be used.</p>
                    {/if}
                  </div>
                {/if}
              {/if}
              {#if !readOnly && !extractedData.isCreditNote}
                <label class="flex items-center gap-2 mb-3 text-xs text-gray-700 cursor-pointer">
                  <input type="checkbox" bind:checked={markAsPaid} class="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span>{qboOverheadMode ? 'Already paid (post as Expense)' : 'Mark as Paid'}</span>
                </label>
              {/if}
              {#if currentBill?.possibleDuplicates?.length && !readOnly}
                <div class="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <p class="font-semibold">Possible duplicate</p>
                  {#each currentBill.possibleDuplicates as d}
                    <p>{d.reason === 'same-file' ? 'Same file as' : 'Same supplier and invoice number as'} {d.fileName || d.uploadId} ({d.status}{d.uploadedAt ? ', uploaded ' + String(d.uploadedAt).slice(0, 10) : ''})</p>
                  {/each}
                  <p class="mt-1">Check it isn't the same bill before syncing.</p>
                </div>
              {/if}
              {#if readOnly}
                <div class="w-full px-4 py-2.5 text-sm font-medium rounded-md bg-gray-100 text-gray-500 text-center">
                  View Only — Already Synced
                </div>
                <p class="mt-2 text-xs text-gray-600">
                  Voiding this bill in Xero or deleting it in JobTread does not reset Buildzilla.
                </p>
                <button
                  on:click={() => clearSyncedStatus()}
                  disabled={clearingSynced}
                  class="mt-2 w-full px-4 py-2 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {clearingSynced ? 'Clearing…' : 'Clear synced status to push again'}
                </button>
              {:else if showSubmitForApproval}
                <button
                  on:click={submitBillForApproval}
                  disabled={!readyToSync}
                  class="w-full px-4 py-2.5 text-sm font-medium rounded-md transition-colors
                    {readyToSync ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}"
                >
                  {#if submittingForApproval}
                    <span class="flex items-center justify-center">
                      <svg class="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  {:else}
                    Submit for Approval
                  {/if}
                </button>
              {:else}
                <button
                  on:click={syncBoth}
                  disabled={!readyToSync}
                  class="w-full px-4 py-2.5 text-sm font-medium rounded-md transition-colors
                    {readyToSync ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}"
                >
                  {#if syncing}
                    <span class="flex items-center justify-center">
                      <svg class="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {syncProgress.step}
                    </span>
                  {:else if currentBillSynced}
                    Already synced
                  {:else}
                    {accountingOnly ? (qboOverheadMode ? `Send ${qboDocLabel} to QB` : `Send overhead to ${overheadDestShort}`) : isQuickBooks ? 'Sync to JobTread' : 'Sync to Xero + JobTread'}
                  {/if}
                </button>
              {/if}
              {#if !readyToSync && !syncing && !submittingForApproval && !syncLocked}
                <div class="mt-2 text-xs text-gray-500 space-y-0.5">
                  {#if !vendorReady}<p>- Select or match a vendor</p>{/if}
                  {#if qboPaidNeedsAccount}<p>- Choose which account this was paid from</p>{/if}
                  {#if !accountingOnly && !matchedJob && !matchedPO}<p>- Select a job or PO</p>{/if}
                  {#if !accountingOnly && !assignmentsComplete}<p>- {splitMode ? 'Pick a cost group or cost code to split the bill across' : 'Assign all line items to cost items'}</p>{/if}
                  {#if !hasDates}<p>- Issue Date and Due Date are required</p>{/if}
                </div>
              {/if}
              {#if clearedSyncedNote && !readOnly}
                <p class="mt-2 text-xs text-green-700">Synced status cleared. Review the bill and click Sync to push it again.</p>
              {/if}
            </div>
            </div>
            </div>
            {#if syncResults.length > 0}
              <div class="hidden lg:block" bind:this={syncResultsEl}>
                <SyncResults
                  {syncResults}
                  {selectedBills}
                  {overheadDest}
                  {userExpenseCode}
                  {syncing}
                  {accountingOnly}
                  showBack={false}
                  onRetry={retryXeroWithDefaultCode}
                  onLinkExisting={linkToExistingXeroBill}
                  onCreateAnyway={() => syncBoth({ allowDuplicate: true })}
                  onClearSynced={clearSyncedStatus}
                  {clearingSynced}
                  onBack={goBack}
                />
              </div>
            {/if}

          </div>
        </div>
      </div>
    {:else}
      <div class="bg-white rounded-lg shadow p-8 text-center">
        <p class="text-gray-500">Loading bills...</p>
      </div>
    {/if}
  </div>

  {#if duplicatePrompt}
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4" role="dialog" aria-modal="true" aria-labelledby="duplicate-bill-title">
        <h3 id="duplicate-bill-title" class="text-lg font-semibold text-gray-900 mb-2">This invoice is already in Xero</h3>
        <p class="text-sm text-gray-600 mb-4">{duplicatePrompt.message.replace(/ If this really is a different .+?, choose "Create anyway"\./, '')}</p>
        <div class="flex justify-end gap-3">
          <button
            type="button"
            on:click={() => (duplicatePrompt = null)}
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
          >
            Leave it
          </button>
          <button
            type="button"
            on:click={confirmDuplicateSubmit}
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
          >
            Create anyway
          </button>
        </div>
      </div>
    </div>
  {/if}
</Layout>

<style>
  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
</style>
